// USCIS Accessible About Page - Section 508 Compliant
import React, { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  Tabs,
  Tab,
  Paper,
  Grid,
  IconButton,
  Breadcrumbs,
  Link as MuiLink,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Collapse,
  useMediaQuery,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Container,
  Button,
  Badge,
  Avatar
} from '@mui/material';
import {
  Home as HomeIcon,
  Info as InfoIcon,
  MenuBook as MenuBookIcon,
  History as HistoryIcon,
  People as PeopleIcon,
  Security as SecurityIcon,
  Assignment as AssignmentIcon,
  QuestionAnswer as QuestionAnswerIcon,
  Summarize as SummarizeIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  KeyboardArrowLeft as KeyboardArrowLeftIcon,
  Menu as MenuIcon,
  Lock as LockIcon,
  Description as DocumentationIcon,
  Visibility as VisibilityIcon,
  ExpandMore as ExpandMoreIcon,
  Search as SearchIcon,
  School as SchoolIcon,
  SwapHoriz as SwapHorizIcon,
  VerifiedUser as VerifiedUserIcon,
  Gavel as GavelIcon,
  Speed as SpeedIcon,
  Shield as ShieldIcon,
  SupervisorAccount as SupervisorAccountIcon,
  SecurityUpdate as SecurityUpdateIcon,
  Policy as PolicyIcon,
  FactCheck as FactCheckIcon,
  DeveloperMode as DeveloperModeIcon,
  Timeline as TimelineIcon,
  ManageAccounts as ManageAccountsIcon,
  Category as CategoryIcon,
  AssignmentTurnedIn as AssignmentTurnedInIcon,
  CloudDone as CloudDoneIcon,
  Verified as VerifiedIcon,
  TrendingUp as TrendingUpIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { Link, useNavigate } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';

// Tab Panel component for accessibility
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
  id: string;
}

const TabPanel = (props: TabPanelProps) => {
  const { children, value, index, id, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`${id}-tabpanel-${index}`}
      aria-labelledby={`${id}-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
};

// Side navigation item component for accessibility
interface SideNavItemProps {
  icon: React.ReactElement;
  label: string;
  selected: boolean;
  onClick: () => void;
  index: number;
  tabId: string;
}

function SideNavItem({ icon, label, selected, onClick, index, tabId }: SideNavItemProps) {
  return (
    <ListItemButton
      selected={selected}
      onClick={onClick}
      role="tab"
      aria-selected={selected}
      aria-controls={`${tabId}-tabpanel-${index}`}
      id={`${tabId}-tab-${index}`}
    >
      <ListItemIcon>{icon}</ListItemIcon>
      <ListItemText primary={label} />
    </ListItemButton>
  );
}

function About() {
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState(0);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [sideNavOpen, setSideNavOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [showMobileMenu, setShowMobileMenu] = useState(false);

  // Function to handle tab changes with keyboard accessibility
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const toggleSideNav = () => {
    setSideNavOpen(!sideNavOpen);
  };

  // Function to handle side navigation clicks
  const handleSideNavClick = (index: number) => {
    setTabValue(index);
  };

  // Toggle mobile menu
  const toggleMobileMenu = () => {
    setShowMobileMenu(!showMobileMenu);
  };

  // Tab ID for accessibility
  const tabId = "about-eunify";

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Breadcrumb navigation for improved wayfinding */}
      <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 3 }}>
        <MuiLink
          underline="hover"
          color="inherit"
          href="/dashboard"
          sx={{ display: 'flex', alignItems: 'center' }}
          aria-label="Go to dashboard"
        >
          <HomeIcon sx={{ mr: 0.5 }} fontSize="small" />
          Dashboard
        </MuiLink>
        <Typography
          color="text.primary"
          sx={{
            display: 'flex',
            alignItems: 'center',
            color: '#003366',
            fontWeight: 600
          }}
        >
          <InfoIcon sx={{ mr: 0.5 }} fontSize="small" />
          About
        </Typography>
      </Breadcrumbs>

      {/* Page title - Standard USCIS format */}
      <Box sx={{ mb: 3 }}>
        <Typography 
          variant="h4" 
          component="h1" 
          sx={{
            color: '#003366',
            fontWeight: 'bold'
          }}
        >
          About E-Unify
        </Typography>
      </Box>

      <Box
        sx={{
          p: 3,
          mb: 4,
          backgroundColor: '#f5f5f5',
          borderLeft: '4px solid #003366',
          borderRadius: '4px'
        }}
      >
        <Typography variant="body1">
          E-Unify is USCIS's enterprise metadata management platform that provides a single source of truth for data assets across the organization. This comprehensive solution enables better data discovery, understanding, and governance, ultimately supporting improved decision-making and operational efficiency.
        </Typography>
      </Box>

      {/* Mobile menu toggle button - only visible on small screens */}
      {isMobile && (
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            startIcon={showMobileMenu ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            onClick={toggleMobileMenu}
            variant="outlined"
            sx={{ color: '#003366', borderColor: '#003366' }}
            aria-expanded={showMobileMenu}
            aria-controls="mobile-sidenav"
          >
            {showMobileMenu ? 'Hide Menu' : 'Show Menu'}
          </Button>
        </Box>
      )}

      {/* Main content with tabs and side navigation */}
      <Box sx={{ mb: 2, display: { xs: 'flex', md: 'flex' }, alignItems: 'center', justifyContent: 'space-between' }}>
        <Button
          onClick={toggleSideNav}
          aria-expanded={sideNavOpen}
          aria-label={sideNavOpen ? "Hide side menu" : "Show side menu"}
          startIcon={sideNavOpen ? <KeyboardArrowLeftIcon /> : <MenuIcon />}
          variant="outlined"
          color="primary"
          size="small"
          sx={{ 
            borderColor: '#003366', 
            color: '#003366',
            '&:hover': { backgroundColor: 'rgba(0, 51, 102, 0.04)' },
            '&:focus': { outline: '2px solid #003366', outlineOffset: '2px' }
          }}
        >
          {sideNavOpen ? "Hide Side Menu" : "Show Side Menu"}
        </Button>
      </Box>
      <Grid container spacing={3}>
        {/* Side Navigation - Hidden on mobile unless expanded */}
        <Grid 
          item 
          xs={12} 
          md={sideNavOpen ? 3 : 0}
          sx={{ 
            display: sideNavOpen ? 'block' : 'none',
            transition: 'all 0.3s ease-in-out' 
          }}
        >
          <Button
            fullWidth
            onClick={() => setShowMobileMenu(!showMobileMenu)}
            startIcon={showMobileMenu ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            sx={{ display: { xs: 'flex', md: 'none' }, mb: 1 }}
            aria-expanded={showMobileMenu}
            aria-controls="side-navigation"
          >
            {showMobileMenu ? 'Hide Navigation' : 'Show Navigation'}
          </Button>
          <Collapse in={showMobileMenu || !isMobile}>
            <Paper elevation={3} sx={{ height: '100%' }}>
              <Typography 
                variant="subtitle1" 
                sx={{ 
                  p: 2, 
                  backgroundColor: '#003366', 
                  color: 'white',
                  borderTopLeftRadius: '4px',
                  borderTopRightRadius: '4px',
                }}
              >
                Navigation
              </Typography>
              <List 
                component="div" 
                role="tablist"
                id="side-navigation"
                aria-label="About E-Unify navigation"
                sx={{ '.MuiListItemIcon-root': { color: '#003366' } }}
              >
                <SideNavItem 
                  icon={<SummarizeIcon />} 
                  label="Executive Summary"
                  selected={tabValue === 0}
                  onClick={() => handleSideNavClick(0)}
                  index={0}
                  tabId={tabId}
                />
                <SideNavItem 
                  icon={<InfoIcon />} 
                  label="Overview"
                  selected={tabValue === 1}
                  onClick={() => handleSideNavClick(1)}
                  index={1}
                  tabId={tabId}
                />
                <SideNavItem 
                  icon={<MenuBookIcon />} 
                  label="Mission & Vision"
                  selected={tabValue === 2}
                  onClick={() => handleSideNavClick(2)}
                  index={2}
                  tabId={tabId}
                />
                <SideNavItem 
                  icon={<HistoryIcon />} 
                  label="History & Development"
                  selected={tabValue === 3}
                  onClick={() => handleSideNavClick(3)}
                  index={3}
                  tabId={tabId}
                />
                <SideNavItem 
                  icon={<PeopleIcon />} 
                  label="Team & Organization"
                  selected={tabValue === 4}
                  onClick={() => handleSideNavClick(4)}
                  index={4}
                  tabId={tabId}
                />
                <SideNavItem 
                  icon={<SecurityIcon />} 
                  label="Security & Compliance"
                  selected={tabValue === 5}
                  onClick={() => handleSideNavClick(5)}
                  index={5}
                  tabId={tabId}
                />
                <SideNavItem 
                  icon={<QuestionAnswerIcon />} 
                  label="FAQ"
                  selected={tabValue === 6}
                  onClick={() => handleSideNavClick(6)}
                  index={6}
                  tabId={tabId}
                />
                <SideNavItem 
                  icon={<DocumentationIcon />} 
                  label="Platform Documentation"
                  selected={tabValue === 7}
                  onClick={() => handleSideNavClick(7)}
                  index={7}
                  tabId={tabId}
                />
              </List>
            </Paper>
          </Collapse>
        </Grid>

        {/* Tab Content Area */}
        <Grid 
          item 
          xs={12} 
          md={sideNavOpen ? 9 : 12}
          sx={{ transition: 'all 0.3s ease-in-out' }}
        >
          <Paper elevation={3} sx={{ minHeight: '600px' }}>
            {/* Horizontal tabs - alternative navigation method for accessibility */}
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tabs
                value={tabValue}
                onChange={handleTabChange}
                aria-label="About E-Unify tabs"
                variant="scrollable"
                scrollButtons="auto"
                textColor="primary"
                indicatorColor="primary"
                sx={{ 
                  '.MuiTab-root': { 
                    color: '#003366', 
                    '&.Mui-selected': {
                      color: '#003366',
                      fontWeight: 700
                    }
                  }
                }}
              >
                <Tab 
                  label="Executive Summary" 
                  id={`${tabId}-tab-0`}
                  aria-controls={`${tabId}-tabpanel-0`}
                  icon={<AssignmentTurnedInIcon />}
                  iconPosition="start"
                />
                <Tab 
                  label="Overview" 
                  id={`${tabId}-tab-1`}
                  aria-controls={`${tabId}-tabpanel-1`}
                />
                <Tab 
                  label="Mission & Vision" 
                  id={`${tabId}-tab-2`}
                  aria-controls={`${tabId}-tabpanel-2`}
                />
                <Tab 
                  label="History & Development" 
                  id={`${tabId}-tab-3`}
                  aria-controls={`${tabId}-tabpanel-3`}
                />
                <Tab 
                  label="Team & Organization" 
                  id={`${tabId}-tab-4`}
                  aria-controls={`${tabId}-tabpanel-4`}
                />
                <Tab 
                  label="Security & Compliance" 
                  id={`${tabId}-tab-5`}
                  aria-controls={`${tabId}-tabpanel-5`}
                />
                <Tab 
                  label="FAQ" 
                  id={`${tabId}-tab-6`}
                  aria-controls={`${tabId}-tabpanel-6`}
                />
                <Tab 
                  label="Platform Documentation" 
                  id={`${tabId}-tab-7`}
                  aria-controls={`${tabId}-tabpanel-7`}
                />
              </Tabs>
            </Box>

            {/* Tab Panels */}
            {/* Tab 0: Executive Summary */}
            <TabPanel value={tabValue} index={0} id={tabId}>
              <Typography variant="h5" sx={{ color: '#003366', fontWeight: 600, mb: 3 }}>
                Executive Summary
              </Typography>
              
              <Paper sx={{ p: 3, mb: 3, borderLeft: '4px solid #003366', backgroundColor: '#f8f9fa' }}>
                <Typography variant="h6" gutterBottom sx={{ color: '#003366', fontWeight: 600 }}>
                  What is E-Unify?
                </Typography>
                <Typography paragraph>
                  E-Unify is an enterprise metadata management framework designed for the Department of Homeland Security (DHS), providing centralized data asset documentation, governance, and discovery capabilities across all participating organizations.
                </Typography>
              </Paper>

              <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 3, height: '100%', borderLeft: '4px solid #003366' }}>
                    <Typography variant="h6" gutterBottom sx={{ color: '#003366', fontWeight: 600 }}>
                      Core Features
                    </Typography>
                    <List dense>
                      <ListItem>
                        <ListItemIcon><CheckCircleIcon sx={{ color: '#003366' }} /></ListItemIcon>
                        <ListItemText primary="Business Glossary" secondary="Standardized terminology" />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon><CheckCircleIcon sx={{ color: '#003366' }} /></ListItemIcon>
                        <ListItemText primary="Data Catalog" secondary="Centralized asset discovery" />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon><CheckCircleIcon sx={{ color: '#003366' }} /></ListItemIcon>
                        <ListItemText primary="Data Lineage" secondary="End-to-end traceability" />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon><CheckCircleIcon sx={{ color: '#003366' }} /></ListItemIcon>
                        <ListItemText primary="Policy Management" secondary="Automated compliance" />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon><CheckCircleIcon sx={{ color: '#003366' }} /></ListItemIcon>
                        <ListItemText primary="Workflow Automation" secondary="Streamlined processes" />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon><CheckCircleIcon sx={{ color: '#003366' }} /></ListItemIcon>
                        <ListItemText primary="Impact Analysis" secondary="Change assessment" />
                      </ListItem>
                    </List>
                  </Paper>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 3, height: '100%', borderLeft: '4px solid #B31B1B' }}>
                    <Typography variant="h6" gutterBottom sx={{ color: '#B31B1B', fontWeight: 600 }}>
                      Core Capabilities
                    </Typography>
                    <List dense>
                      <ListItem>
                        <ListItemIcon><CheckCircleIcon sx={{ color: '#B31B1B' }} /></ListItemIcon>
                        <ListItemText primary="High Performance" secondary="10x faster discovery" />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon><CheckCircleIcon sx={{ color: '#B31B1B' }} /></ListItemIcon>
                        <ListItemText primary="Zero Trust Security" secondary="MFA & PIV/CAC support" />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon><CheckCircleIcon sx={{ color: '#B31B1B' }} /></ListItemIcon>
                        <ListItemText primary="FedRAMP Ready" secondary="Government cloud compliance" />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon><CheckCircleIcon sx={{ color: '#B31B1B' }} /></ListItemIcon>
                        <ListItemText primary="508 Compliant" secondary="Full accessibility" />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon><CheckCircleIcon sx={{ color: '#B31B1B' }} /></ListItemIcon>
                        <ListItemText primary="Scalable Architecture" secondary="Millions of assets" />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon><CheckCircleIcon sx={{ color: '#B31B1B' }} /></ListItemIcon>
                        <ListItemText primary="No Vendor Lock-In" secondary="Open standards & APIs" />
                      </ListItem>
                    </List>
                  </Paper>
                </Grid>
              </Grid>

              <Paper sx={{ p: 3, mb: 3, borderLeft: '4px solid #003366' }}>
                <Typography variant="h6" gutterBottom sx={{ color: '#003366', fontWeight: 600 }}>
                  Key Benefits
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={4}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>• Enhanced Data Discovery</Typography>
                    <Typography variant="body2" color="text.secondary">Find assets 10x faster</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>• Improved Compliance</Typography>
                    <Typography variant="body2" color="text.secondary">Automated policy enforcement</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>• Better Collaboration</Typography>
                    <Typography variant="body2" color="text.secondary">Unified terminology</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>• Reduced Redundancy</Typography>
                    <Typography variant="body2" color="text.secondary">Eliminate duplicate efforts</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>• Enterprise Security</Typography>
                    <Typography variant="body2" color="text.secondary">Zero trust architecture</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>• Future-Proof</Typography>
                    <Typography variant="body2" color="text.secondary">Open standards</Typography>
                  </Grid>
                </Grid>
              </Paper>

              <Paper 
                sx={{ 
                  p: 4, 
                  textAlign: 'center', 
                  backgroundColor: '#003366',
                  color: 'white'
                }}
              >
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                  Ready to Transform Your Data Management?
                </Typography>
                <Typography variant="body2" paragraph>
                  Contact us to learn how E-Unify can enhance your organization's data governance
                </Typography>
                <Button
                  variant="contained"
                  size="large"
                  onClick={() => navigate('/contact')}
                  sx={{
                    bgcolor: 'white',
                    color: '#003366',
                    '&:hover': {
                      bgcolor: '#f0f0f0'
                    }
                  }}
                >
                  Contact Us
                </Button>
              </Paper>
            </TabPanel>

            {/* Tab 1: Overview */}
            <TabPanel value={tabValue} index={1} id={tabId}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                <Typography variant="h5" sx={{ color: '#003366', fontWeight: 600 }}>
                  Overview of
                </Typography>
                <img 
                  src="/images/eunify-m.png" 
                  alt="E-Unify" 
                  style={{ height: 35, width: 'auto' }}
                />
              </Box>
              
              <Typography paragraph>
                E-Unify is a proposed enterprise metadata management framework designed for Department of Homeland Security (DHS) components and communities. Initially conceived for U.S. Immigration and Customs Enforcement (ICE) and U.S. Citizenship and Immigration Services (USCIS), the framework has potential for DHS-wide implementation. E-Unify aims to centralize data asset documentation, governance, and discovery capabilities across all participating organizations.
              </Typography>
              
              <Typography paragraph>
                The proposed framework would enhance data understanding across DHS by providing context, lineage, and quality metrics for connected data assets. E-Unify is designed to establish a foundation for improved data management practices, enabling better decision-making, reducing redundancy, and fostering collaboration between technical and business teams across DHS components. The framework supports metadata-driven automation, semantic search, and integration with enterprise systems.
              </Typography>

              <Typography variant="h6" gutterBottom sx={{ mt: 3, color: '#003366', fontWeight: 600 }} id="branding-section-heading">
                About the E-Unify Name and Tagline
              </Typography>
              
              <Paper
                elevation={2}
                sx={{
                  p: 3,
                  mb: 4,
                  borderLeft: '4px solid #003366',
                  backgroundColor: '#f8f9fa',
                  position: 'relative'
                }}
                aria-labelledby="branding-section-heading"
              >
                {/* Logo in top right corner */}
                <Box 
                  sx={{ 
                    position: 'absolute',
                    top: 16,
                    right: 16,
                    p: 2, 
                    bgcolor: 'white',
                    border: '2px solid #003366',
                    borderRadius: 2,
                    display: { xs: 'none', md: 'flex' },
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 2px 8px rgba(0,51,102,0.1)',
                    maxWidth: 220
                  }}
                  role="img"
                  aria-labelledby="branding-logo-label"
                >
                  <Typography id="branding-logo-label" sx={{ width: '1px', height: '1px', overflow: 'hidden', position: 'absolute' }}>
                    E-Unify logo with tagline Your Pathway to Benefit
                  </Typography>
                  <img 
                    src="/images/eunify-m.png" 
                    alt="E-Unify - Your Pathway to Benefit" 
                    style={{ width: '100%', height: 'auto' }}
                  />
                </Box>

                <Grid container spacing={3}>
                  <Grid item xs={12} md={8}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#003366', mb: 1 }} id="branding-tagline">
                      "E-Unify: Your Pathway to Benefit"
                    </Typography>
                    
                    <Typography paragraph>
                      The name "E-Unify" embodies the core mission of unifying enterprise data across Department of Homeland Security (DHS) components and communities. The "E" represents both "Enterprise" and "Electronic," reflecting a digital approach to data integration. "Unify" speaks to the commitment to bringing together disparate data sources into a cohesive, accessible framework that can serve all DHS organizations.
                    </Typography>
                    
                    <Typography paragraph>
                      The tagline, "Your Pathway to Benefit," emphasizes the framework's ultimate purpose: creating clear pathways for users to derive tangible benefits from organizational data. It highlights that E-Unify is designed not just as a technical tool, but as a means to unlock value and enable better outcomes for DHS stakeholders across all participating components.
                    </Typography>
                    
                    <Box 
                      sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', alignItems: 'center' }}
                      role="figure"
                      aria-labelledby="branding-formation-label"
                    >
                      <Typography id="branding-formation-label" sx={{ width: '1px', height: '1px', overflow: 'hidden', position: 'absolute' }}>
                        Visual representation of how E-Unify name is formed
                      </Typography>
                      <Chip 
                        label="Enterprise" 
                        color="primary" 
                        sx={{ mr: 1, mb: { xs: 1, sm: 0 }, bgcolor: '#003366', fontSize: '0.875rem' }} 
                        aria-label="Enterprise"
                        tabIndex={0}
                      />
                      <Typography variant="body2" sx={{ mx: 1 }} aria-hidden="true">+</Typography>
                      <Chip 
                        label="Unify" 
                        sx={{ mr: 1, mb: { xs: 1, sm: 0 }, bgcolor: '#0056b3', color: 'white', fontSize: '0.875rem' }} 
                        aria-label="Unify"
                        tabIndex={0}
                      />
                      <Typography variant="body2" sx={{ mx: 1 }} aria-hidden="true">=</Typography>
                      <Chip 
                        label="E-Unify" 
                        sx={{ fontWeight: 'bold', bgcolor: '#003366', color: 'white', fontSize: '0.875rem' }} 
                        aria-label="E-Unify"
                        tabIndex={0}
                      />
                    </Box>
                  </Grid>
                  
                  {/* Mobile logo - shown below text on small screens */}
                  <Grid item xs={12} sx={{ display: { xs: 'flex', md: 'none' }, justifyContent: 'center', mt: 2 }}>
                    <Box 
                      sx={{ 
                        p: 2, 
                        bgcolor: 'white',
                        border: '2px solid #003366',
                        borderRadius: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: '0 2px 8px rgba(0,51,102,0.1)',
                        maxWidth: 250
                      }}
                      role="img"
                      aria-label="E-Unify logo with tagline Your Pathway to Benefit"
                    >
                      <img 
                        src="/images/eunify-m.png" 
                        alt="E-Unify - Your Pathway to Benefit" 
                        style={{ width: '100%', height: 'auto' }}
                      />
                    </Box>
                  </Grid>
                </Grid>
                
                {/* Accessibility features for screen readers */}
                <Box className="sr-only" sx={{ position: 'absolute', width: '1px', height: '1px', padding: 0, margin: '-1px', overflow: 'hidden', clip: 'rect(0,0,0,0)', whiteSpace: 'nowrap', borderWidth: 0 }}>
                  <Typography variant="body2">
                    The E-Unify branding combines Enterprise and Unify concepts, with the official tagline "Your Pathway to Benefit" to emphasize how the platform creates pathways for users to benefit from organizational data.
                  </Typography>
                </Box>
              </Paper>
              
              {/* Core Features Section */}
              <Typography variant="h6" gutterBottom sx={{ mt: 4, mb: 3, color: '#003366', fontWeight: 600 }}>
                Core Features
              </Typography>
              
              <Grid container spacing={3} sx={{ mb: 4 }}>
                {/* Business Glossary */}
                <Grid item xs={12} md={6}>
                  <Card 
                    sx={{ 
                      height: '100%', 
                      borderLeft: '4px solid #003366',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: 4
                      }
                    }}
                    onClick={() => navigate('/features/business-glossary')}
                    role="button"
                    tabIndex={0}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        navigate('/features/business-glossary');
                      }
                    }}
                    aria-label="View Business Glossary details"
                  >
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <MenuBookIcon sx={{ color: '#003366', mr: 1, fontSize: 28 }} />
                        <Typography variant="h6" sx={{ color: '#003366', fontWeight: 600 }}>
                          Business Glossary
                        </Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        Standardized terminology and definitions for business concepts across the organization, ensuring consistent understanding of key terms.
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Data Catalog */}
                <Grid item xs={12} md={6}>
                  <Card 
                    sx={{ 
                      height: '100%', 
                      borderLeft: '4px solid #003366',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: 4
                      }
                    }}
                    onClick={() => navigate('/features/data-catalog-detail')}
                    role="button"
                    tabIndex={0}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        navigate('/features/data-catalog-detail');
                      }
                    }}
                    aria-label="View Data Catalog details"
                  >
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <CategoryIcon sx={{ color: '#003366', mr: 1, fontSize: 28 }} />
                        <Typography variant="h6" sx={{ color: '#003366', fontWeight: 600 }}>
                          Data Catalog
                        </Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        Comprehensive inventory of data assets with detailed metadata, making data discoverable and understandable for all users.
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Data Lineage */}
                <Grid item xs={12} md={6}>
                  <Card 
                    sx={{ 
                      height: '100%', 
                      borderLeft: '4px solid #003366',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: 4
                      }
                    }}
                    onClick={() => navigate('/features/data-lineage')}
                    role="button"
                    tabIndex={0}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        navigate('/features/data-lineage');
                      }
                    }}
                    aria-label="View Data Lineage details"
                  >
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <TimelineIcon sx={{ color: '#003366', mr: 1, fontSize: 28 }} />
                        <Typography variant="h6" sx={{ color: '#003366', fontWeight: 600 }}>
                          Data Lineage
                        </Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        Visual tracking of data flow from source to consumption, providing transparency into transformations and dependencies.
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Policy Management */}
                <Grid item xs={12} md={6}>
                  <Card 
                    sx={{ 
                      height: '100%', 
                      borderLeft: '4px solid #003366',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: 4
                      }
                    }}
                    onClick={() => navigate('/features/policy-management')}
                    role="button"
                    tabIndex={0}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        navigate('/features/policy-management');
                      }
                    }}
                    aria-label="View Policy Management details"
                  >
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <PolicyIcon sx={{ color: '#003366', mr: 1, fontSize: 28 }} />
                        <Typography variant="h6" sx={{ color: '#003366', fontWeight: 600 }}>
                          Policy Management
                        </Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        Documentation and enforcement of data governance policies, standards, and procedures across the enterprise.
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Workflow Automation */}
                <Grid item xs={12} md={6}>
                  <Card 
                    sx={{ 
                      height: '100%', 
                      borderLeft: '4px solid #003366',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: 4
                      }
                    }}
                    onClick={() => navigate('/features/workflow-automation')}
                    role="button"
                    tabIndex={0}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        navigate('/features/workflow-automation');
                      }
                    }}
                    aria-label="View Workflow Automation details"
                  >
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <SwapHorizIcon sx={{ color: '#003366', mr: 1, fontSize: 28 }} />
                        <Typography variant="h6" sx={{ color: '#003366', fontWeight: 600 }}>
                          Workflow Automation
                        </Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        Streamlined processes for metadata curation, approvals, and change management to ensure data quality.
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Impact Analysis */}
                <Grid item xs={12} md={6}>
                  <Card 
                    sx={{ 
                      height: '100%', 
                      borderLeft: '4px solid #003366',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: 4
                      }
                    }}
                    onClick={() => navigate('/features/impact-analysis')}
                    role="button"
                    tabIndex={0}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        navigate('/features/impact-analysis');
                      }
                    }}
                    aria-label="View Impact Analysis details"
                  >
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <AssignmentIcon sx={{ color: '#003366', mr: 1, fontSize: 28 }} />
                        <Typography variant="h6" sx={{ color: '#003366', fontWeight: 600 }}>
                          Impact Analysis
                        </Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        Assessment capabilities to understand the downstream effects of proposed data changes before implementation.
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
              
              {/* Core Capabilities Section */}
              <Typography variant="h6" gutterBottom sx={{ mt: 6, mb: 3, color: '#003366', fontWeight: 600 }}>
                Core Capabilities
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Technical foundations enabling our Core Features
              </Typography>
              
              <Grid container spacing={3} sx={{ mb: 4 }}>
                {/* High Performance */}
                <Grid item xs={12} md={6}>
                  <Card 
                    sx={{ 
                      height: '100%', 
                      borderLeft: '4px solid #003366',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: 4
                      }
                    }}
                    onClick={() => navigate('/capabilities/high-performance')}
                    role="button"
                    tabIndex={0}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        navigate('/capabilities/high-performance');
                      }
                    }}
                    aria-label="View High Performance details"
                  >
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <SpeedIcon sx={{ color: '#003366', mr: 1, fontSize: 28 }} />
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          High Performance
                        </Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        10x faster data discovery with optimized search and caching
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Zero Trust Security */}
                <Grid item xs={12} md={6}>
                  <Card 
                    sx={{ 
                      height: '100%', 
                      borderLeft: '4px solid #B31B1B',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: 4
                      }
                    }}
                    onClick={() => navigate('/capabilities/zero-trust-security')}
                    role="button"
                    tabIndex={0}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        navigate('/capabilities/zero-trust-security');
                      }
                    }}
                    aria-label="View Zero Trust Security details"
                  >
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <ShieldIcon sx={{ color: '#B31B1B', mr: 1, fontSize: 28 }} />
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          Zero Trust Security
                        </Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        Multi-factor authentication, PIV/CAC support, continuous verification
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>

                {/* FedRAMP Ready */}
                <Grid item xs={12} md={6}>
                  <Card 
                    sx={{ 
                      height: '100%', 
                      borderLeft: '4px solid #003366',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: 4
                      }
                    }}
                    onClick={() => navigate('/capabilities/fedramp-ready')}
                    role="button"
                    tabIndex={0}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        navigate('/capabilities/fedramp-ready');
                      }
                    }}
                    aria-label="View FedRAMP Ready details"
                  >
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <CloudDoneIcon sx={{ color: '#003366', mr: 1, fontSize: 28 }} />
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          FedRAMP Ready
                        </Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        Built for FedRAMP High authorization with government-approved cloud services
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>

                {/* 508 Compliant */}
                <Grid item xs={12} md={6}>
                  <Card 
                    sx={{ 
                      height: '100%', 
                      borderLeft: '4px solid #0076D6',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: 4
                      }
                    }}
                    onClick={() => navigate('/capabilities/section-508-compliant')}
                    role="button"
                    tabIndex={0}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        navigate('/capabilities/section-508-compliant');
                      }
                    }}
                    aria-label="View 508 Compliant details"
                  >
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <VerifiedIcon sx={{ color: '#0076D6', mr: 1, fontSize: 28 }} />
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          508 Compliant
                        </Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        Full Section 508 and WCAG 2.0 Level AA accessibility compliance
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Scalable Architecture */}
                <Grid item xs={12} md={6}>
                  <Card 
                    sx={{ 
                      height: '100%', 
                      borderLeft: '4px solid #003366',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: 4
                      }
                    }}
                    onClick={() => navigate('/capabilities/scalable-architecture')}
                    role="button"
                    tabIndex={0}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        navigate('/capabilities/scalable-architecture');
                      }
                    }}
                    aria-label="View Scalable Architecture details"
                  >
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <TrendingUpIcon sx={{ color: '#003366', mr: 1, fontSize: 28 }} />
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          Scalable Architecture
                        </Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        Handles millions of data assets across hundreds of systems
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>

                {/* No Vendor Lock-In */}
                <Grid item xs={12} md={6}>
                  <Card 
                    sx={{ 
                      height: '100%', 
                      borderLeft: '4px solid #B31B1B',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: 4
                      }
                    }}
                    onClick={() => navigate('/capabilities/no-vendor-lock-in')}
                    role="button"
                    tabIndex={0}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        navigate('/capabilities/no-vendor-lock-in');
                      }
                    }}
                    aria-label="View No Vendor Lock-In details"
                  >
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <CheckCircleIcon sx={{ color: '#B31B1B', mr: 1, fontSize: 28 }} />
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          No Vendor Lock-In
                        </Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        Open standards and APIs ensure flexibility and future-proofing
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
              
              {/* Contextual callout to Data Strategy Support Services */}
              <Paper
                elevation={2}
                sx={{
                  p: 3,
                  mb: 4,
                  backgroundColor: '#e6f2ff',
                  borderLeft: '4px solid #003366',
                  borderRadius: '4px'
                }}
                role="region"
                aria-label="Data Strategy Support Services callout"
              >
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} md={8}>
                    <Typography variant="h6" sx={{ color: '#003366', fontWeight: 600 }} gutterBottom>
                      Data Strategy Support Services
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Explore our 508-compliant Data Strategy Support module featuring the Operating Model, Capstone Project, and curated resources to help you plan and execute your agency data strategy.
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={4} sx={{ display: 'flex', justifyContent: { xs: 'flex-start', md: 'flex-end' } }}>
                    <Button
                      component={Link}
                      to="/data-strategy-support"
                      variant="contained"
                      color="primary"
                      aria-label="Open Data Strategy Support Services"
                    >
                      Open Data Strategy
                    </Button>
                  </Grid>
                </Grid>
              </Paper>
            </TabPanel>

            {/* Tab 2: Mission & Vision */}
            <TabPanel value={tabValue} index={2} id={tabId}>
              <Typography variant="h5" gutterBottom sx={{ color: '#003366', fontWeight: 600 }}>
                Mission & Vision
              </Typography>
              
              <Box 
                sx={{
                  p: 3,
                  mb: 4,
                  backgroundColor: '#e6f7ff',
                  borderLeft: '4px solid #003366',
                  borderRadius: '4px'
                }}
              >
                <Typography variant="h6" gutterBottom sx={{ color: '#003366' }}>
                  Mission Statement
                </Typography>
                <Typography>
                  To establish a unified, trusted source of metadata that empowers USCIS personnel to efficiently discover, understand, and leverage the agency's data assets for improved operational outcomes and informed decision-making.
                </Typography>
              </Box>
              
              <Box 
                sx={{
                  p: 3,
                  mb: 4,
                  backgroundColor: '#e6f7ff',
                  borderLeft: '4px solid #003366',
                  borderRadius: '4px'
                }}
              >
                <Typography variant="h6" gutterBottom sx={{ color: '#003366' }}>
                  Vision Statement
                </Typography>
                <Typography>
                  To transform USCIS into a data-driven organization where all stakeholders can seamlessly access accurate, consistent metadata to enhance data quality, promote reuse, and foster innovation while ensuring security and compliance with federal regulations.
                </Typography>
              </Box>
              
              <Typography variant="h6" gutterBottom sx={{ mt: 4, color: '#003366', fontWeight: 600 }}>
                Strategic Objectives
              </Typography>
              
              <Box component="ul" sx={{ pl: 3 }}>
                <Box component="li" sx={{ mb: 2 }}>
                  <Typography><strong>Improve Data Discovery:</strong> Enable users to quickly find relevant data assets across the enterprise through intuitive search and navigation capabilities.</Typography>
                </Box>
                <Box component="li" sx={{ mb: 2 }}>
                  <Typography><strong>Enhance Data Understanding:</strong> Provide comprehensive context for data assets including definitions, relationships, quality metrics, and usage guidelines.</Typography>
                </Box>
                <Box component="li" sx={{ mb: 2 }}>
                  <Typography><strong>Strengthen Data Governance:</strong> Establish and enforce consistent policies, standards, and procedures for data management across USCIS.</Typography>
                </Box>
                <Box component="li" sx={{ mb: 2 }}>
                  <Typography><strong>Support Regulatory Compliance:</strong> Maintain documentation and traceability to demonstrate adherence to federal data regulations and security requirements.</Typography>
                </Box>
                <Box component="li" sx={{ mb: 2 }}>
                  <Typography><strong>Promote Data Reuse:</strong> Reduce redundancy and inconsistency by making existing data assets visible and accessible to all authorized users.</Typography>
                </Box>
                <Box component="li" sx={{ mb: 2 }}>
                  <Typography><strong>Enable Data-Driven Decision Making:</strong> Provide the metadata foundation necessary for advanced analytics and reporting capabilities.</Typography>
                </Box>
              </Box>
              
              <Typography variant="h6" gutterBottom sx={{ mt: 4, color: '#003366', fontWeight: 600 }}>
                Guiding Principles
              </Typography>
              
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12} sm={6}>
                  <Paper 
                    elevation={2} 
                    sx={{ 
                      p: 2, 
                      height: '100%', 
                      borderTop: '4px solid #003366',
                      display: 'flex',
                      flexDirection: 'column'
                    }}
                  >
                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, color: '#003366' }}>
                      User-Centered Design
                    </Typography>
                    <Typography variant="body2">
                      The platform is designed around user needs, with intuitive interfaces and workflows that accommodate both technical and non-technical users.
                    </Typography>
                  </Paper>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <Paper 
                    elevation={2} 
                    sx={{ 
                      p: 2, 
                      height: '100%', 
                      borderTop: '4px solid #003366',
                      display: 'flex',
                      flexDirection: 'column'
                    }}
                  >
                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, color: '#003366' }}>
                      Enterprise Perspective
                    </Typography>
                    <Typography variant="body2">
                      Decisions about metadata management prioritize the needs of the entire organization over those of individual departments or systems.
                    </Typography>
                  </Paper>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <Paper 
                    elevation={2} 
                    sx={{ 
                      p: 2, 
                      height: '100%', 
                      borderTop: '4px solid #003366',
                      display: 'flex',
                      flexDirection: 'column'
                    }}
                  >
                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, color: '#003366' }}>
                      Data as an Asset
                    </Typography>
                    <Typography variant="body2">
                      Data is treated as a valuable organizational asset that requires proper documentation, management, and protection throughout its lifecycle.
                    </Typography>
                  </Paper>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <Paper 
                    elevation={2} 
                    sx={{ 
                      p: 2, 
                      height: '100%', 
                      borderTop: '4px solid #003366',
                      display: 'flex',
                      flexDirection: 'column'
                    }}
                  >
                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, color: '#003366' }}>
                      Continuous Improvement
                    </Typography>
                    <Typography variant="body2">
                      The platform evolves based on user feedback, emerging requirements, and technological advancements to maintain its relevance and effectiveness.
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>
            </TabPanel>
            
            {/* Tab 3: History & Development */}
            <TabPanel value={tabValue} index={3} id={tabId}>
              <Typography variant="h5" gutterBottom sx={{ color: '#003366', fontWeight: 600 }}>
                History & Development of E-Unify
              </Typography>
              
              <Typography paragraph>
                E-Unify has evolved through multiple phases of development to become USCIS's comprehensive metadata management platform. Below is a timeline of key milestones in its evolution.
              </Typography>
              
              <Box sx={{ mb: 4 }}>
                <TableContainer component={Paper} sx={{ mb: 4, boxShadow: 3 }}>
                  <Table aria-label="E-Unify development timeline">
                    <TableHead sx={{ backgroundColor: '#003366' }}>
                      <TableRow>
                        <TableCell sx={{ color: 'white', fontWeight: 600 }}>Year</TableCell>
                        <TableCell sx={{ color: 'white', fontWeight: 600 }}>Phase</TableCell>
                        <TableCell sx={{ color: 'white', fontWeight: 600 }}>Key Developments</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        <TableCell>2019</TableCell>
                        <TableCell>Planning & Research</TableCell>
                        <TableCell>
                          <Typography variant="body2">Initial assessment of metadata management needs across USCIS. Market research and evaluation of potential platforms. Business case development and funding approval.</Typography>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>2020</TableCell>
                        <TableCell>Pilot Implementation</TableCell>
                        <TableCell>
                          <Typography variant="body2">Limited rollout with core business glossary functionality. Integration with key data systems. Initial user testing and feedback collection.</Typography>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>2021</TableCell>
                        <TableCell>Platform Expansion</TableCell>
                        <TableCell>
                          <Typography variant="body2">Addition of data catalog capabilities. Development of data lineage visualization. Improved search functionality and user interface based on pilot feedback.</Typography>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>2022</TableCell>
                        <TableCell>Enterprise Adoption</TableCell>
                        <TableCell>
                          <Typography variant="body2">Full agency rollout. Integration with additional enterprise systems. Implementation of role-based access controls. Introduction of policy management features.</Typography>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>2023</TableCell>
                        <TableCell>Feature Enhancement</TableCell>
                        <TableCell>
                          <Typography variant="body2">Advanced workflow automation. Data quality monitoring capabilities. Expanded reporting and analytics. Mobile-responsive design implementation.</Typography>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>2024-2025</TableCell>
                        <TableCell>Continuous Evolution</TableCell>
                        <TableCell>
                          <Typography variant="body2">Implementation of AI-assisted metadata suggestions. Enhanced integration capabilities. Improved compliance monitoring tools. Section 508 accessibility enhancements.</Typography>
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
              
              <Typography variant="h6" gutterBottom sx={{ mt: 4, color: '#003366', fontWeight: 600 }}>
                Development Methodology
              </Typography>
              
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Paper
                    elevation={2}
                    sx={{
                      p: 3,
                      height: '100%',
                      borderLeft: '4px solid #003366',
                    }}
                  >
                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, color: '#003366' }}>
                      Agile Development
                    </Typography>
                    <Typography paragraph>
                      E-Unify was developed using Agile methodologies with 2-week sprints. This iterative approach allowed for regular stakeholder feedback and continuous refinement of features based on user needs.
                    </Typography>
                    <Typography variant="body2">
                      Key practices included:
                    </Typography>
                    <Box component="ul" sx={{ pl: 3 }}>
                      <Box component="li" sx={{ mb: 1 }}>
                        <Typography>Regular sprint reviews with stakeholders</Typography>
                      </Box>
                      <Box component="li" sx={{ mb: 1 }}>
                        <Typography>Continuous integration and deployment</Typography>
                      </Box>
                      <Box component="li" sx={{ mb: 1 }}>
                        <Typography>User story-driven development</Typography>
                      </Box>
                      <Box component="li" sx={{ mb: 1 }}>
                        <Typography>Cross-functional development teams</Typography>
                      </Box>
                    </Box>
                  </Paper>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Paper
                    elevation={2}
                    sx={{
                      p: 3,
                      height: '100%',
                      borderLeft: '4px solid #003366',
                    }}
                  >
                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, color: '#003366' }}>
                      User-Centered Design
                    </Typography>
                    <Typography paragraph>
                      Throughout development, E-Unify maintained a strong focus on user-centered design principles to ensure the platform was intuitive and effective for diverse user roles.
                    </Typography>
                    <Typography variant="body2">
                      Design practices included:
                    </Typography>
                    <Box component="ul" sx={{ pl: 3 }}>
                      <Box component="li" sx={{ mb: 1 }}>
                        <Typography>User research and persona development</Typography>
                      </Box>
                      <Box component="li" sx={{ mb: 1 }}>
                        <Typography>Usability testing throughout development</Typography>
                      </Box>
                      <Box component="li" sx={{ mb: 1 }}>
                        <Typography>Accessibility considerations from day one</Typography>
                      </Box>
                      <Box component="li" sx={{ mb: 1 }}>
                        <Typography>Iterative refinement based on user feedback</Typography>
                      </Box>
                    </Box>
                  </Paper>
                </Grid>
              </Grid>
              
              <Typography variant="h6" gutterBottom sx={{ mt: 4, color: '#003366', fontWeight: 600 }}>
                Key Implementation Challenges
              </Typography>
              
              <Paper
                elevation={3}
                sx={{
                  p: 3,
                  mt: 2,
                  backgroundColor: '#f8f8f8',
                }}
              >
                <Typography paragraph>
                  The implementation of E-Unify across USCIS required overcoming several significant challenges:
                </Typography>
                
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 2 }}>
                  Technical Challenges
                </Typography>
                <Box component="ul" sx={{ pl: 3 }}>
                  <Box component="li" sx={{ mb: 1 }}>
                    <Typography><strong>System Integration:</strong> Connecting with numerous legacy systems using diverse technologies</Typography>
                  </Box>
                  <Box component="li" sx={{ mb: 1 }}>
                    <Typography><strong>Data Volume:</strong> Managing metadata for millions of data elements across the organization</Typography>
                  </Box>
                  <Box component="li" sx={{ mb: 1 }}>
                    <Typography><strong>Performance:</strong> Ensuring responsive search and navigation with large metadata repositories</Typography>
                  </Box>
                  <Box component="li" sx={{ mb: 1 }}>
                    <Typography><strong>Security:</strong> Implementing robust security while maintaining accessibility</Typography>
                  </Box>
                </Box>
                
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 2 }}>
                  Organizational Challenges
                </Typography>
                <Box component="ul" sx={{ pl: 3 }}>
                  <Box component="li" sx={{ mb: 1 }}>
                    <Typography><strong>Change Management:</strong> Facilitating adoption across diverse departments</Typography>
                  </Box>
                  <Box component="li" sx={{ mb: 1 }}>
                    <Typography><strong>Metadata Governance:</strong> Establishing standards and ownership</Typography>
                  </Box>
                  <Box component="li" sx={{ mb: 1 }}>
                    <Typography><strong>Training:</strong> Educating users with varied technical backgrounds</Typography>
                  </Box>
                  <Box component="li" sx={{ mb: 1 }}>
                    <Typography><strong>Resource Allocation:</strong> Balancing development priorities with limited resources</Typography>
                  </Box>
                </Box>
                
                <Typography paragraph sx={{ mt: 2 }}>
                  Despite these challenges, the development team implemented mitigation strategies including phased rollout, comprehensive user training, regular stakeholder engagement, and adaptive development practices to ensure successful platform adoption across USCIS.
                </Typography>
              </Paper>
            </TabPanel>

            {/* Tab 4: Team & Organization */}
            <TabPanel value={tabValue} index={4} id={tabId}>
              <Typography variant="h5" gutterBottom sx={{ color: '#003366', fontWeight: 600 }}>
                Team & Organization
              </Typography>
              
              <Typography paragraph>
                E-Unify is developed and maintained by a dedicated team of data governance professionals, software engineers, and metadata specialists. Our team brings together expertise in enterprise data management, federal IT systems, and user experience design.
              </Typography>
              
              <Typography variant="h6" gutterBottom sx={{ mt: 2, color: '#003366', fontWeight: 600 }}>
                Organizational Structure
              </Typography>
              
              <Grid container spacing={4} sx={{ mb: 4 }}>
                <Grid item xs={12} md={6}>
                  <Paper
                    elevation={3}
                    sx={{
                      p: 3,
                      height: '100%',
                      borderTop: '6px solid #003366',
                      display: 'flex',
                      flexDirection: 'column'
                    }}
                  >
                    <Box>
                      <Typography variant="h6" gutterBottom sx={{ color: '#003366', fontWeight: 600 }}>
                        Leadership & Oversight
                      </Typography>
                      <Box component="ul" sx={{ pl: 2 }}>
                        <Box component="li" sx={{ mb: 1 }}>
                          <Typography><strong>Executive Sponsor:</strong> USCIS Chief Data Officer</Typography>
                        </Box>
                        <Box component="li" sx={{ mb: 1 }}>
                          <Typography><strong>Program Director:</strong> Metadata Governance Lead</Typography>
                        </Box>
                        <Box component="li" sx={{ mb: 1 }}>
                          <Typography><strong>Technical Lead:</strong> Enterprise Architect</Typography>
                        </Box>
                        <Box component="li" sx={{ mb: 1 }}>
                          <Typography><strong>Advisory Board:</strong> Cross-functional directors</Typography>
                        </Box>
                      </Box>
                    </Box>
                    
                  </Paper>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Paper
                    elevation={3}
                    sx={{
                      p: 3,
                      height: '100%',
                      borderTop: '6px solid #003366',
                      display: 'flex',
                      flexDirection: 'column'
                    }}
                  >
                    <Typography variant="h6" gutterBottom sx={{ color: '#003366', fontWeight: 600 }}>
                      Operational Teams
                    </Typography>
                    <Box component="ul" sx={{ pl: 2 }}>
                      <Box component="li" sx={{ mb: 1 }}>
                        <Typography><strong>Metadata Stewards:</strong> Subject matter experts from each business domain</Typography>
                      </Box>
                      <Box component="li" sx={{ mb: 1 }}>
                        <Typography><strong>Development Team:</strong> Software engineers and UX designers</Typography>
                      </Box>
                      <Box component="li" sx={{ mb: 1 }}>
                        <Typography><strong>Data Governance Council:</strong> Cross-functional data policy group</Typography>
                      </Box>
                      <Box component="li" sx={{ mb: 1 }}>
                        <Typography><strong>Training & Adoption:</strong> Change management specialists</Typography>
                      </Box>
                    </Box>
                  </Paper>
                </Grid>
                
              </Grid>
              
              <Typography variant="h6" gutterBottom sx={{ mt: 4, color: '#003366', fontWeight: 600 }}>
                Governance Structure
              </Typography>
              
              <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={12} md={4}>
                  <Paper
                    elevation={3}
                    sx={{
                      p: 3,
                      height: '100%',
                      borderLeft: '4px solid #B31B1B'
                    }}
                  >
                    <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600, color: '#B31B1B' }}>
                      Strategic Governance
                    </Typography>
                    <Box component="ul" sx={{ pl: 2 }}>
                      <Box component="li" sx={{ mb: 1 }}>
                        <Typography variant="body2"><strong>Data Executive Board</strong> - Approves enterprise metadata standards</Typography>
                      </Box>
                      <Box component="li" sx={{ mb: 1 }}>
                        <Typography variant="body2"><strong>Metadata Steering Committee</strong> - Sets strategic direction</Typography>
                      </Box>
                      <Box component="li" sx={{ mb: 1 }}>
                        <Typography variant="body2"><strong>Budget & Resource Planning</strong> - Ensures program sustainability</Typography>
                      </Box>
                    </Box>
                  </Paper>
                </Grid>
                
                <Grid item xs={12} md={4}>
                  <Paper
                    elevation={3}
                    sx={{
                      p: 3,
                      height: '100%',
                      borderLeft: '4px solid #003366'
                    }}
                  >
                    <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600, color: '#003366' }}>
                      Tactical Governance
                    </Typography>
                    <Box component="ul" sx={{ pl: 2 }}>
                      <Box component="li" sx={{ mb: 1 }}>
                        <Typography variant="body2"><strong>Domain Working Groups</strong> - Specialized domain expertise</Typography>
                      </Box>
                      <Box component="li" sx={{ mb: 1 }}>
                        <Typography variant="body2"><strong>Metadata Standards Group</strong> - Develops data element definitions</Typography>
                      </Box>
                      <Box component="li" sx={{ mb: 1 }}>
                        <Typography variant="body2"><strong>Technical Architecture Team</strong> - Integration standards and APIs</Typography>
                      </Box>
                    </Box>
                  </Paper>
                </Grid>
              </Grid>
              
              <Typography variant="h6" gutterBottom sx={{ mt: 4, color: '#003366', fontWeight: 600 }}>
                Key Team Roles
              </Typography>
              
              <Grid container spacing={2} sx={{ mb: 4 }}>
                <Grid item xs={12} sm={6} md={3}>
                  <Paper elevation={2} sx={{ p: 2, height: '100%', borderRadius: '8px', bgcolor: '#f5f5f5' }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                      <Typography variant="subtitle1" gutterBottom sx={{ color: '#003366', fontWeight: 600 }}>
                        Data Stewards
                      </Typography>
                      <Typography variant="body2">
                        Subject matter experts responsible for ensuring metadata quality within their business domains.
                      </Typography>
                    </Box>
                  </Paper>
                </Grid>
                
                <Grid item xs={12} sm={6} md={3}>
                  <Paper elevation={2} sx={{ p: 2, height: '100%', borderRadius: '8px', bgcolor: '#f5f5f5' }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                      <Typography variant="subtitle1" gutterBottom sx={{ color: '#003366', fontWeight: 600 }}>
                        Platform Engineers
                      </Typography>
                      <Typography variant="body2">
                        Technical specialists responsible for E-Unify's infrastructure, performance, and integrations.
                      </Typography>
                    </Box>
                  </Paper>
                </Grid>
                
                <Grid item xs={12} sm={6} md={3}>
                  <Paper elevation={2} sx={{ p: 2, height: '100%', borderRadius: '8px', bgcolor: '#f5f5f5' }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                      <Typography variant="subtitle1" gutterBottom sx={{ color: '#003366', fontWeight: 600 }}>
                        Data Architects
                      </Typography>
                      <Typography variant="body2">
                        Experts who design metadata models, taxonomies, and relationship structures.
                      </Typography>
                    </Box>
                  </Paper>
                </Grid>
                
                <Grid item xs={12} sm={6} md={3}>
                  <Paper elevation={2} sx={{ p: 2, height: '100%', borderRadius: '8px', bgcolor: '#f5f5f5' }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                      <Typography variant="subtitle1" gutterBottom sx={{ color: '#003366', fontWeight: 600 }}>
                        Training Specialists
                      </Typography>
                      <Typography variant="body2">
                        Educators who develop learning materials and conduct training on metadata practices.
                      </Typography>
                    </Box>
                  </Paper>
                </Grid>
              </Grid>
            </TabPanel>
            
            {/* Tab 5: Security & Compliance */}
            <TabPanel value={tabValue} index={5} id={tabId}>
              <Typography variant="h5" gutterBottom sx={{ color: '#003366', fontWeight: 600 }}>
                Security & Compliance
              </Typography>
              
              <Typography paragraph>
                Security and compliance are foundational principles of the E-Unify platform, ensuring protection of sensitive USCIS data while maintaining regulatory alignment.
              </Typography>
              
              <Typography variant="h6" gutterBottom sx={{ mt: 3, color: '#003366', fontWeight: 600 }}>
                Security Framework
              </Typography>
              
              <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={12} sm={6} md={3}>
                  <Card 
                    elevation={2} 
                    sx={{ 
                      height: '100%', 
                      display: 'flex', 
                      flexDirection: 'column',
                      borderRadius: '8px',
                      overflow: 'hidden'
                    }}
                  >
                    <Box sx={{ bgcolor: '#003366', p: 2, display: 'flex', alignItems: 'center' }}>
                      <SearchIcon sx={{ color: 'white', mr: 1 }} />
                      <Typography variant="subtitle1" sx={{ color: 'white', fontWeight: 600 }}>
                        Enhanced Discoverability
                      </Typography>
                    </Box>
                    <CardContent sx={{ flexGrow: 1, pt: 2 }}>
                      <Typography variant="body2">
                        Implement powerful search capabilities and intuitive navigation to reduce the time spent locating relevant data assets by 50% by the end of 2025.
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                
                <Grid item xs={12} sm={6} md={3}>
                  <Card 
                    elevation={2} 
                    sx={{ 
                      height: '100%', 
                      display: 'flex', 
                      flexDirection: 'column',
                      borderRadius: '8px',
                      overflow: 'hidden'
                    }}
                  >
                    <Box sx={{ bgcolor: '#003366', p: 2, display: 'flex', alignItems: 'center' }}>
                      <SchoolIcon sx={{ color: 'white', mr: 1 }} />
                      <Typography variant="subtitle1" sx={{ color: 'white', fontWeight: 600 }}>
                        Organization-wide Adoption
                      </Typography>
                    </Box>
                    <CardContent sx={{ flexGrow: 1, pt: 2 }}>
                      <Typography variant="body2">
                        Achieve 90% active usage across all USCIS directorates through comprehensive training, intuitive interfaces, and demonstrated value in daily workflows.
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                
                <Grid item xs={12} sm={6} md={3}>
                  <Card 
                    elevation={2} 
                    sx={{ 
                      height: '100%', 
                      display: 'flex', 
                      flexDirection: 'column',
                      borderRadius: '8px',
                      overflow: 'hidden'
                    }}
                  >
                    <Box sx={{ bgcolor: '#003366', p: 2, display: 'flex', alignItems: 'center' }}>
                      <SwapHorizIcon sx={{ color: 'white', mr: 1 }} />
                      <Typography variant="subtitle1" sx={{ color: 'white', fontWeight: 600 }}>
                        System Integration
                      </Typography>
                    </Box>
                    <CardContent sx={{ flexGrow: 1, pt: 2 }}>
                      <Typography variant="body2">
                        Connect with all critical USCIS systems and data repositories, creating a comprehensive network of metadata that spans the entire data ecosystem.
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                
                <Grid item xs={12} sm={6} md={3}>
                  <Card 
                    elevation={2} 
                    sx={{ 
                      height: '100%', 
                      display: 'flex', 
                      flexDirection: 'column',
                      borderRadius: '8px',
                      overflow: 'hidden'
                    }}
                  >
                    <Box sx={{ bgcolor: '#003366', p: 2, display: 'flex', alignItems: 'center' }}>
                      <VerifiedUserIcon sx={{ color: 'white', mr: 1 }} />
                      <Typography variant="subtitle1" sx={{ color: 'white', fontWeight: 600 }}>
                        Data Quality Improvement
                      </Typography>
                    </Box>
                    <CardContent sx={{ flexGrow: 1, pt: 2 }}>
                      <Typography variant="body2">
                        Leverage metadata management to drive measurable improvements in data quality, with comprehensive metrics tracking accuracy, completeness, and consistency.
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
              
              <Typography variant="h6" gutterBottom sx={{ mt: 4, color: '#003366', fontWeight: 600 }}>
                Alignment with USCIS Strategic Goals
              </Typography>
              
              <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                      <GavelIcon sx={{ color: '#003366', mr: 2, mt: 0.5 }} />
                      <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#003366' }}>
                          Strengthening the Security and Integrity of the Immigration System
                        </Typography>
                        <Typography variant="body2" paragraph>
                          E-Unify supports this goal by ensuring data quality and accessibility, enabling more accurate verification and vetting processes through standardized metadata and data lineage tracking.
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                      <SpeedIcon sx={{ color: '#003366', mr: 2, mt: 0.5 }} />
                      <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#003366' }}>
                          Improving Operational Efficiency
                        </Typography>
                        <Typography variant="body2" paragraph>
                          By providing clear data definitions, ownership, and relationships, E-Unify reduces duplicative efforts, streamlines data access, and enables more efficient analysis and decision-making processes.
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                  
                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                      <PeopleIcon sx={{ color: '#003366', mr: 2, mt: 0.5 }} />
                      <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#003366' }}>
                          Enhancing Customer Experience
                        </Typography>
                        <Typography variant="body2" paragraph>
                          E-Unify improves customer experience indirectly by enabling USCIS staff to access accurate data quickly, resulting in more responsive service, consistent information across touchpoints, and reduced processing times.
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
              </Paper>
            </TabPanel>

            {/* Tab 3: Team & Organization */}
            <TabPanel value={tabValue} index={3} id={tabId}>
              <Typography variant="h5" gutterBottom sx={{ color: '#003366', fontWeight: 600 }}>
                Team & Organization
              </Typography>
              
              <Typography paragraph>
                The E-Unify platform is supported by a dedicated team of professionals committed to delivering enterprise metadata management excellence. Our organizational structure ensures comprehensive coverage of technical development, user experience, training, and governance.
              </Typography>
              
              <Typography variant="h6" gutterBottom sx={{ mt: 4, mb: 3, color: '#003366', fontWeight: 600 }}>
                Organizational Structure
              </Typography>
              
              <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={12} md={6} lg={3}>
                  <Card elevation={3} sx={{ height: '100%', borderTop: '4px solid #003366' }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 2 }}>
                        <Avatar sx={{ width: 60, height: 60, bgcolor: '#003366', mb: 1 }}>
                          <SupervisorAccountIcon />
                        </Avatar>
                        <Typography variant="h6" align="center" sx={{ fontWeight: 600 }}>
                          Executive Leadership
                        </Typography>
                      </Box>
                      <Typography variant="body2" paragraph>
                        Strategic direction and resource allocation for the E-Unify platform, ensuring alignment with USCIS objectives.
                      </Typography>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mt: 2 }}>
                        Key Responsibilities:
                      </Typography>
                      <Box component="ul" sx={{ pl: 2, mt: 1 }}>
                        <Box component="li" sx={{ mb: 0.5 }}>
                          <Typography variant="body2">Strategic planning</Typography>
                        </Box>
                        <Box component="li" sx={{ mb: 0.5 }}>
                          <Typography variant="body2">Cross-agency coordination</Typography>
                        </Box>
                        <Box component="li" sx={{ mb: 0.5 }}>
                          <Typography variant="body2">Budget oversight</Typography>
                        </Box>
                        <Box component="li" sx={{ mb: 0.5 }}>
                          <Typography variant="body2">Performance accountability</Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
                
                <Grid item xs={12} md={6} lg={3}>
                  <Card elevation={3} sx={{ height: '100%', borderTop: '4px solid #003366' }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 2 }}>
                        <Avatar sx={{ width: 60, height: 60, bgcolor: '#003366', mb: 1 }}>
                          <DeveloperModeIcon />
                        </Avatar>
                        <Typography variant="h6" align="center" sx={{ fontWeight: 600 }}>
                          Technical Team
                        </Typography>
                      </Box>
                      <Typography variant="body2" paragraph>
                        Engineers and developers responsible for platform functionality, integrations, and technical architecture.
                      </Typography>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mt: 2 }}>
                        Key Responsibilities:
                      </Typography>
                      <Box component="ul" sx={{ pl: 2, mt: 1 }}>
                        <Box component="li" sx={{ mb: 0.5 }}>
                          <Typography variant="body2">Platform development</Typography>
                        </Box>
                        <Box component="li" sx={{ mb: 0.5 }}>
                          <Typography variant="body2">System integrations</Typography>
                        </Box>
                        <Box component="li" sx={{ mb: 0.5 }}>
                          <Typography variant="body2">Performance optimization</Typography>
                        </Box>
                        <Box component="li" sx={{ mb: 0.5 }}>
                          <Typography variant="body2">Security implementation</Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
                
                <Grid item xs={12} md={6} lg={3}>
                  <Card elevation={3} sx={{ height: '100%', borderTop: '4px solid #003366' }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 2 }}>
                        <Avatar sx={{ width: 60, height: 60, bgcolor: '#003366', mb: 1 }}>
                          <PeopleIcon />
                        </Avatar>
                        <Typography variant="h6" align="center" sx={{ fontWeight: 600 }}>
                          User Experience
                        </Typography>
                      </Box>
                      <Typography variant="body2" paragraph>
                        Design and usability specialists focused on creating an intuitive, accessible interface for all users.
                      </Typography>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mt: 2 }}>
                        Key Responsibilities:
                      </Typography>
                      <Box component="ul" sx={{ pl: 2, mt: 1 }}>
                        <Box component="li" sx={{ mb: 0.5 }}>
                          <Typography variant="body2">Interface design</Typography>
                        </Box>
                        <Box component="li" sx={{ mb: 0.5 }}>
                          <Typography variant="body2">Accessibility compliance</Typography>
                        </Box>
                        <Box component="li" sx={{ mb: 0.5 }}>
                          <Typography variant="body2">Usability testing</Typography>
                        </Box>
                        <Box component="li" sx={{ mb: 0.5 }}>
                          <Typography variant="body2">User research</Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
                
                <Grid item xs={12} md={6} lg={3}>
                  <Card elevation={3} sx={{ height: '100%', borderTop: '4px solid #003366' }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 2 }}>
                        <Avatar sx={{ width: 60, height: 60, bgcolor: '#003366', mb: 1 }}>
                          <SchoolIcon />
                        </Avatar>
                        <Typography variant="h6" align="center" sx={{ fontWeight: 600 }}>
                          Training & Support
                        </Typography>
                      </Box>
                      <Typography variant="body2" paragraph>
                        Specialists dedicated to user onboarding, ongoing education, and responsive support services.
                      </Typography>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mt: 2 }}>
                        Key Responsibilities:
                      </Typography>
                      <Box component="ul" sx={{ pl: 2, mt: 1 }}>
                        <Box component="li" sx={{ mb: 0.5 }}>
                          <Typography variant="body2">Training development</Typography>
                        </Box>
                        <Box component="li" sx={{ mb: 0.5 }}>
                          <Typography variant="body2">Help desk support</Typography>
                        </Box>
                        <Box component="li" sx={{ mb: 0.5 }}>
                          <Typography variant="body2">Documentation</Typography>
                        </Box>
                        <Box component="li" sx={{ mb: 0.5 }}>
                          <Typography variant="body2">User community management</Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
              
              <Typography variant="h6" gutterBottom sx={{ mt: 4, mb: 3, color: '#003366', fontWeight: 600 }}>
                Governance Structure
              </Typography>
              
              <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#003366', mb: 2 }}>
                      Executive Steering Committee
                    </Typography>
                    <Typography variant="body2" paragraph>
                      Senior leaders from across USCIS who provide strategic guidance and resource prioritization for the E-Unify platform. The committee meets quarterly to review progress and set direction.
                    </Typography>
                    <Typography variant="body2">
                      <strong>Members include:</strong> Chief Data Officer, CIO, representatives from major operational directorates, and key business stakeholders.
                    </Typography>
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#003366', mb: 2 }}>
                      Metadata Governance Council
                    </Typography>
                    <Typography variant="body2" paragraph>
                      Cross-functional team responsible for metadata standards, quality controls, and organizational adoption. The council meets monthly to address ongoing governance needs.
                    </Typography>
                    <Typography variant="body2">
                      <strong>Members include:</strong> Data stewards, business analysts, technical leads, and subject matter experts from key operational areas.
                    </Typography>
                  </Grid>
                  
                  <Grid item xs={12} sx={{ mt: 2 }}>
                    <Divider sx={{ my: 2 }} />
                    
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#003366', mb: 2 }}>
                      Governance Framework
                    </Typography>
                    
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6} md={3}>
                        <Box sx={{ border: '1px solid #e0e0e0', p: 2, borderRadius: 1 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                            <PolicyIcon fontSize="small" sx={{ mr: 1, color: '#003366', verticalAlign: 'middle' }} />
                            Policies & Standards
                          </Typography>
                          <Typography variant="body2">
                            Formalized rules and guidelines governing metadata management and platform usage.
                          </Typography>
                        </Box>
                      </Grid>
                      
                      <Grid item xs={12} sm={6} md={3}>
                        <Box sx={{ border: '1px solid #e0e0e0', p: 2, borderRadius: 1 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                            <AssignmentIcon fontSize="small" sx={{ mr: 1, color: '#003366', verticalAlign: 'middle' }} />
                            Roles & Responsibilities
                          </Typography>
                          <Typography variant="body2">
                            Clearly defined ownership and accountability for metadata quality and management.
                          </Typography>
                        </Box>
                      </Grid>
                      
                      <Grid item xs={12} sm={6} md={3}>
                        <Box sx={{ border: '1px solid #e0e0e0', p: 2, borderRadius: 1 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                            <FactCheckIcon fontSize="small" sx={{ mr: 1, color: '#003366', verticalAlign: 'middle' }} />
                            Quality Controls
                          </Typography>
                          <Typography variant="body2">
                            Processes and metrics to ensure metadata accuracy, completeness, and consistency.
                          </Typography>
                        </Box>
                      </Grid>
                      
                      <Grid item xs={12} sm={6} md={3}>
                        <Box sx={{ border: '1px solid #e0e0e0', p: 2, borderRadius: 1 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                            <TimelineIcon fontSize="small" sx={{ mr: 1, color: '#003366', verticalAlign: 'middle' }} />
                            Change Management
                          </Typography>
                          <Typography variant="body2">
                            Structured approach to transitions in metadata definitions, relationships, and platform features.
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </Paper>
              
              <Typography variant="h6" gutterBottom sx={{ mt: 4, color: '#003366', fontWeight: 600 }}>
                Collaboration with Stakeholders
              </Typography>
              
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Paper elevation={2} sx={{ p: 3, height: '100%', borderLeft: '4px solid #003366' }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#003366' }}>
                      Cross-Directorate Engagement
                    </Typography>
                    <Typography variant="body2" paragraph>
                      The E-Unify team works closely with representatives from all USCIS directorates to ensure the platform meets diverse operational needs. Regular engagement sessions include:
                    </Typography>
                    <Box component="ul" sx={{ pl: 2 }}>
                      <Box component="li" sx={{ mb: 0.5 }}>
                        <Typography variant="body2">Quarterly stakeholder reviews</Typography>
                      </Box>
                      <Box component="li" sx={{ mb: 0.5 }}>
                        <Typography variant="body2">Monthly user group meetings</Typography>
                      </Box>
                      <Box component="li" sx={{ mb: 0.5 }}>
                        <Typography variant="body2">Embedded liaison officers in key directorates</Typography>
                      </Box>
                      <Box component="li" sx={{ mb: 0.5 }}>
                        <Typography variant="body2">Feedback collection through surveys and interviews</Typography>
                      </Box>
                    </Box>
                  </Paper>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Paper elevation={2} sx={{ p: 3, height: '100%', borderLeft: '4px solid #003366' }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#003366' }}>
                      External Partnerships
                    </Typography>
                    <Typography variant="body2" paragraph>
                      The team maintains strategic partnerships to enhance platform capabilities and ensure alignment with broader government standards:
                    </Typography>
                    <Box component="ul" sx={{ pl: 2 }}>
                      <Box component="li" sx={{ mb: 0.5 }}>
                        <Typography variant="body2">DHS Data Management Working Group</Typography>
                      </Box>
                      <Box component="li" sx={{ mb: 0.5 }}>
                        <Typography variant="body2">Federal CDO Council</Typography>
                      </Box>
                      <Box component="li" sx={{ mb: 0.5 }}>
                        <Typography variant="body2">Industry standards bodies</Typography>
                      </Box>
                      <Box component="li" sx={{ mb: 0.5 }}>
                        <Typography variant="body2">Technology vendor collaborations</Typography>
                      </Box>
                    </Box>
                  </Paper>
                </Grid>
              </Grid>
            </TabPanel>

            {/* Tab 4: Security & Compliance */}
            <TabPanel value={tabValue} index={4} id={tabId}>
              <Typography variant="h5" gutterBottom sx={{ color: '#003366', fontWeight: 600 }}>
                Security & Compliance
              </Typography>
              
              <Typography paragraph>
                E-Unify adheres to the highest standards of security and compliance in accordance with federal regulations and USCIS policies. Our comprehensive approach ensures data protection, privacy, and regulatory compliance.
              </Typography>
              
              <Typography variant="h6" gutterBottom sx={{ mt: 4, mb: 3, color: '#003366', fontWeight: 600 }}>
                Security Framework
              </Typography>
              
              <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={12} md={6}>
                  <Paper elevation={3} sx={{ p: 3, height: '100%', borderLeft: '4px solid #003366' }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                      <ShieldIcon sx={{ color: '#003366', mr: 2, fontSize: 36 }} />
                      <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#003366', mb: 1 }}>
                          Access Control & Authentication
                        </Typography>
                        <Typography variant="body2" paragraph>
                          E-Unify implements a robust role-based access control (RBAC) system that ensures users can only access metadata and features appropriate to their role and responsibilities.
                        </Typography>
                        <Box component="ul" sx={{ pl: 2, mt: 1 }}>
                          <Box component="li" sx={{ mb: 1 }}>
                            <Typography variant="body2"><strong>Multi-factor authentication</strong> required for all users</Typography>
                          </Box>
                          <Box component="li" sx={{ mb: 1 }}>
                            <Typography variant="body2"><strong>Integration with USCIS Identity Management System</strong> for centralized access control</Typography>
                          </Box>
                          <Box component="li" sx={{ mb: 1 }}>
                            <Typography variant="body2"><strong>Granular permission settings</strong> at the community, domain, and asset levels</Typography>
                          </Box>
                          <Box component="li" sx={{ mb: 1 }}>
                            <Typography variant="body2"><strong>Regular access reviews</strong> to ensure proper authorization</Typography>
                          </Box>
                        </Box>
                      </Box>
                    </Box>
                  </Paper>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Paper elevation={3} sx={{ p: 3, height: '100%', borderLeft: '4px solid #003366' }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                      <LockIcon sx={{ color: '#003366', mr: 2, fontSize: 36 }} />
                      <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#003366', mb: 1 }}>
                          Data Protection & Encryption
                        </Typography>
                        <Typography variant="body2" paragraph>
                          All data within E-Unify is protected through comprehensive encryption and security measures that meet federal standards for data protection.
                        </Typography>
                        <Box component="ul" sx={{ pl: 2, mt: 1 }}>
                          <Box component="li" sx={{ mb: 1 }}>
                            <Typography variant="body2"><strong>Encryption at rest</strong> for all stored metadata</Typography>
                          </Box>
                          <Box component="li" sx={{ mb: 1 }}>
                            <Typography variant="body2"><strong>TLS 1.3 encryption</strong> for all data in transit</Typography>
                          </Box>
                          <Box component="li" sx={{ mb: 1 }}>
                            <Typography variant="body2"><strong>Secure API endpoints</strong> with strict authentication requirements</Typography>
                          </Box>
                          <Box component="li" sx={{ mb: 1 }}>
                            <Typography variant="body2"><strong>Regular security assessments</strong> including penetration testing</Typography>
                          </Box>
                        </Box>
                      </Box>
                    </Box>
                  </Paper>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Paper elevation={3} sx={{ p: 3, height: '100%', borderLeft: '4px solid #003366' }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                      <VisibilityIcon sx={{ color: '#003366', mr: 2, fontSize: 36 }} />
                      <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#003366', mb: 1 }}>
                          Monitoring & Auditing
                        </Typography>
                        <Typography variant="body2" paragraph>
                          Comprehensive monitoring and auditing capabilities track all user activities and system changes to ensure accountability and enable incident investigation.
                        </Typography>
                        <Box component="ul" sx={{ pl: 2, mt: 1 }}>
                          <Box component="li" sx={{ mb: 1 }}>
                            <Typography variant="body2"><strong>Complete audit trails</strong> of all metadata changes</Typography>
                          </Box>
                          <Box component="li" sx={{ mb: 1 }}>
                            <Typography variant="body2"><strong>User activity logging</strong> for security monitoring</Typography>
                          </Box>
                          <Box component="li" sx={{ mb: 1 }}>
                            <Typography variant="body2"><strong>Automated alerts</strong> for suspicious activities</Typography>
                          </Box>
                          <Box component="li" sx={{ mb: 1 }}>
                            <Typography variant="body2"><strong>Retention policies</strong> aligned with federal requirements</Typography>
                          </Box>
                        </Box>
                      </Box>
                    </Box>
                  </Paper>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Paper elevation={3} sx={{ p: 3, height: '100%', borderLeft: '4px solid #003366' }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                      <SecurityUpdateIcon sx={{ color: '#003366', mr: 2, fontSize: 36 }} />
                      <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#003366', mb: 1 }}>
                          Vulnerability Management
                        </Typography>
                        <Typography variant="body2" paragraph>
                          Proactive identification and remediation of security vulnerabilities ensures the ongoing protection of the E-Unify platform and its data.
                        </Typography>
                        <Box component="ul" sx={{ pl: 2, mt: 1 }}>
                          <Box component="li" sx={{ mb: 1 }}>
                            <Typography variant="body2"><strong>Regular security scanning</strong> of all platform components</Typography>
                          </Box>
                          <Box component="li" sx={{ mb: 1 }}>
                            <Typography variant="body2"><strong>Prioritized vulnerability remediation</strong> based on risk assessment</Typography>
                          </Box>
                          <Box component="li" sx={{ mb: 1 }}>
                            <Typography variant="body2"><strong>Scheduled patching</strong> with minimal service disruption</Typography>
                          </Box>
                          <Box component="li" sx={{ mb: 1 }}>
                            <Typography variant="body2"><strong>Secure development practices</strong> including code review and testing</Typography>
                          </Box>
                        </Box>
                      </Box>
                    </Box>
                  </Paper>
                </Grid>
              </Grid>
              
              <Typography variant="h6" gutterBottom sx={{ mt: 4, mb: 3, color: '#003366', fontWeight: 600 }}>
                Regulatory Compliance
              </Typography>
              
              <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={4}>
                    <Box sx={{ textAlign: 'center', mb: 2 }}>
                      <Avatar sx={{ width: 70, height: 70, bgcolor: '#003366', mx: 'auto', mb: 2 }}>
                        <PolicyIcon sx={{ fontSize: 40 }} />
                      </Avatar>
                      <Typography variant="h6" sx={{ fontWeight: 600, color: '#003366' }}>
                        Federal Requirements
                      </Typography>
                    </Box>
                    <Box component="ul" sx={{ pl: 2 }}>
                      <Box component="li" sx={{ mb: 1 }}>
                        <Typography variant="body2"><strong>FISMA compliance</strong> with continuous monitoring</Typography>
                      </Box>
                      <Box component="li" sx={{ mb: 1 }}>
                        <Typography variant="body2"><strong>NIST 800-53</strong> security controls implementation</Typography>
                      </Box>
                      <Box component="li" sx={{ mb: 1 }}>
                        <Typography variant="body2"><strong>FedRAMP</strong> security assessment framework</Typography>
                      </Box>
                      <Box component="li" sx={{ mb: 1 }}>
                        <Typography variant="body2"><strong>Section 508 compliance</strong> for accessibility</Typography>
                      </Box>
                    </Box>
                  </Grid>
                  
                  <Grid item xs={12} md={4}>
                    <Box sx={{ textAlign: 'center', mb: 2 }}>
                      <Avatar sx={{ width: 70, height: 70, bgcolor: '#003366', mx: 'auto', mb: 2 }}>
                        <GavelIcon sx={{ fontSize: 40 }} />
                      </Avatar>
                      <Typography variant="h6" sx={{ fontWeight: 600, color: '#003366' }}>
                        Privacy Regulations
                      </Typography>
                    </Box>
                    <Box component="ul" sx={{ pl: 2 }}>
                      <Box component="li" sx={{ mb: 1 }}>
                        <Typography variant="body2"><strong>Privacy Act of 1974</strong> requirements</Typography>
                      </Box>
                      <Box component="li" sx={{ mb: 1 }}>
                        <Typography variant="body2"><strong>E-Government Act</strong> Privacy Impact Assessments</Typography>
                      </Box>
                      <Box component="li" sx={{ mb: 1 }}>
                        <Typography variant="body2"><strong>OMB Circular A-130</strong> privacy provisions</Typography>
                      </Box>
                      <Box component="li" sx={{ mb: 1 }}>
                        <Typography variant="body2"><strong>DHS Privacy Policy</strong> directives</Typography>
                      </Box>
                    </Box>
                  </Grid>
                  
                  <Grid item xs={12} md={4}>
                    <Box sx={{ textAlign: 'center', mb: 2 }}>
                      <Avatar sx={{ width: 70, height: 70, bgcolor: '#003366', mx: 'auto', mb: 2 }}>
                        <FactCheckIcon sx={{ fontSize: 40 }} />
                      </Avatar>
                      <Typography variant="h6" sx={{ fontWeight: 600, color: '#003366' }}>
                        Certification & Authorization
                      </Typography>
                    </Box>
                    <Box component="ul" sx={{ pl: 2 }}>
                      <Box component="li" sx={{ mb: 1 }}>
                        <Typography variant="body2"><strong>Authority to Operate (ATO)</strong> certification</Typography>
                      </Box>
                      <Box component="li" sx={{ mb: 1 }}>
                        <Typography variant="body2"><strong>Annual security assessments</strong> and recertification</Typography>
                      </Box>
                      <Box component="li" sx={{ mb: 1 }}>
                        <Typography variant="body2"><strong>Continuous monitoring</strong> program</Typography>
                      </Box>
                      <Box component="li" sx={{ mb: 1 }}>
                        <Typography variant="body2"><strong>Plan of Action & Milestones (POA&M)</strong> tracking</Typography>
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
              </Paper>
              
              <Typography variant="h6" gutterBottom sx={{ mt: 4, mb: 3, color: '#003366', fontWeight: 600 }}>
                Data Governance Controls
              </Typography>
              
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <Card elevation={2} sx={{ height: '100%' }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Badge
                          color="primary"
                          badgeContent="1"
                          sx={{ '& .MuiBadge-badge': { bgcolor: '#003366', color: 'white' } }}
                        >
                          <Avatar sx={{ bgcolor: 'rgba(0, 51, 102, 0.1)', color: '#003366' }}>
                            <ManageAccountsIcon />
                          </Avatar>
                        </Badge>
                        <Typography variant="subtitle1" sx={{ ml: 2, fontWeight: 600 }}>
                          Data Ownership
                        </Typography>
                      </Box>
                      <Typography variant="body2">
                        Clear assignment of data owners and stewards responsible for metadata quality and governance enforcement.
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                
                <Grid item xs={12} sm={6} md={3}>
                  <Card elevation={2} sx={{ height: '100%' }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Badge
                          color="primary"
                          badgeContent="2"
                          sx={{ '& .MuiBadge-badge': { bgcolor: '#003366', color: 'white' } }}
                        >
                          <Avatar sx={{ bgcolor: 'rgba(0, 51, 102, 0.1)', color: '#003366' }}>
                            <CategoryIcon />
                          </Avatar>
                        </Badge>
                        <Typography variant="subtitle1" sx={{ ml: 2, fontWeight: 600 }}>
                          Classification
                        </Typography>
                      </Box>
                      <Typography variant="body2">
                        Metadata tagging for sensitivity level, protection requirements, and handling procedures.
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                
                <Grid item xs={12} sm={6} md={3}>
                  <Card elevation={2} sx={{ height: '100%' }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Badge
                          color="primary"
                          badgeContent="3"
                          sx={{ '& .MuiBadge-badge': { bgcolor: '#003366', color: 'white' } }}
                        >
                          <Avatar sx={{ bgcolor: 'rgba(0, 51, 102, 0.1)', color: '#003366' }}>
                            <HistoryIcon />
                          </Avatar>
                        </Badge>
                        <Typography variant="subtitle1" sx={{ ml: 2, fontWeight: 600 }}>
                          Lineage Tracking
                        </Typography>
                      </Box>
                      <Typography variant="body2">
                        End-to-end data flow visualization showing origins, transformations, and dependencies for compliance verification.
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                
                <Grid item xs={12} sm={6} md={3}>
                  <Card elevation={2} sx={{ height: '100%' }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Badge
                          color="primary"
                          badgeContent="4"
                          sx={{ '& .MuiBadge-badge': { bgcolor: '#003366', color: 'white' } }}
                        >
                          <Avatar sx={{ bgcolor: 'rgba(0, 51, 102, 0.1)', color: '#003366' }}>
                            <AssignmentTurnedInIcon />
                          </Avatar>
                        </Badge>
                        <Typography variant="subtitle1" sx={{ ml: 2, fontWeight: 600 }}>
                          Compliance Reporting
                        </Typography>
                      </Box>
                      <Typography variant="body2">
                        Automated compliance reporting capabilities for audit readiness and regulatory requirements.
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </TabPanel>
            
            {/* Tab 6: FAQ */}
            <TabPanel value={tabValue} index={6} id={tabId}>
              <Typography variant="h5" gutterBottom sx={{ color: '#003366', fontWeight: 600 }}>
                Frequently Asked Questions
              </Typography>
              
              <Typography paragraph>
                Find answers to common questions about E-Unify's features, access, and support. If you don't see your question answered below, please contact the E-Unify support team.
              </Typography>
              
              <Box sx={{ mt: 3 }}>
                <Typography variant="h6" gutterBottom sx={{ color: '#003366', fontWeight: 600 }}>
                  General Questions
                </Typography>
                
                <Accordion>
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls="panel1-content"
                    id="panel1-header"
                  >
                    <Typography sx={{ fontWeight: 500 }}>What is E-Unify?</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography paragraph>
                      E-Unify is USCIS's enterprise metadata management platform that provides a single source of truth for data assets across the organization. It serves as a comprehensive solution for data discovery, understanding, and governance.
                    </Typography>
                    <Typography>
                      Key capabilities include:
                    </Typography>
                    <Box component="ul" sx={{ pl: 3 }}>
                      <Box component="li" sx={{ mb: 1 }}>
                        <Typography>Business glossary for standardized terminology</Typography>
                      </Box>
                      <Box component="li" sx={{ mb: 1 }}>
                        <Typography>Data catalog for asset discovery</Typography>
                      </Box>
                      <Box component="li" sx={{ mb: 1 }}>
                        <Typography>Data lineage visualization</Typography>
                      </Box>
                      <Box component="li" sx={{ mb: 1 }}>
                        <Typography>Policy management</Typography>
                      </Box>
                      <Box component="li" sx={{ mb: 1 }}>
                        <Typography>Workflow automation</Typography>
                      </Box>
                    </Box>
                  </AccordionDetails>
                </Accordion>
                
                <Accordion>
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls="panel2-content"
                    id="panel2-header"
                  >
                    <Typography sx={{ fontWeight: 500 }}>How do I get access to E-Unify?</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography paragraph>
                      Access to E-Unify is role-based and managed through the agency's identity management system. To request access:
                    </Typography>
                    <Box component="ol" sx={{ pl: 3 }}>
                      <Box component="li" sx={{ mb: 1 }}>
                        <Typography>Complete the E-Unify Access Request Form available on the USCIS intranet</Typography>
                      </Box>
                      <Box component="li" sx={{ mb: 1 }}>
                        <Typography>Include your required role(s) and business justification</Typography>
                      </Box>
                      <Box component="li" sx={{ mb: 1 }}>
                        <Typography>Obtain supervisor approval</Typography>
                      </Box>
                      <Box component="li" sx={{ mb: 1 }}>
                        <Typography>Submit to the E-Unify administrator at <MuiLink href="mailto:eunify-admin@uscis.dhs.gov">eunify-admin@uscis.dhs.gov</MuiLink></Typography>
                      </Box>
                    </Box>
                    <Typography paragraph sx={{ mt: 2 }}>
                      Access approval typically takes 3-5 business days. Training may be required depending on your assigned role.
                    </Typography>
                  </AccordionDetails>
                </Accordion>
              </Box>
            </TabPanel>

            {/* Tab 7: Platform Documentation */}
            <TabPanel value={tabValue} index={7} id={tabId}>
              <Typography variant="h5" gutterBottom sx={{ color: '#003366', fontWeight: 600 }}>
                Platform Documentation
              </Typography>
              
              <Typography paragraph>
                Comprehensive documentation for the E-Unify Data Governance Platform, including recent updates, features, and technical specifications.
              </Typography>

              <Box sx={{ 
                maxHeight: '70vh', 
                overflow: 'auto', 
                border: '1px solid #e0e0e0', 
                borderRadius: 2, 
                p: 3,
                backgroundColor: '#fafafa'
              }}>
                <Typography variant="h6" gutterBottom sx={{ color: '#003366', fontWeight: 600 }}>
                  README - Unidatum Data Governance Platform
                </Typography>
                
                <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
                  Last Updated: September 27, 2025
                </Typography>

                <Typography paragraph>
                  A comprehensive React-based data governance platform designed to improve data literacy and governance within organizations. This enterprise-grade application helps teams discover, understand, and trust their data with intelligent governance solutions, advanced analytics, and AI/ML insights.
                </Typography>

                <Typography variant="h6" gutterBottom sx={{ color: '#003366', fontWeight: 600, mt: 3 }}>
                  September 2025: Major Platform Enhancements & Optimizations
                </Typography>

                <Typography variant="subtitle1" gutterBottom sx={{ color: '#003366', fontWeight: 600, mt: 2 }}>
                  Data Domains Management System
                </Typography>
                <Box component="ul" sx={{ pl: 3, mb: 2 }}>
                  <Box component="li" sx={{ mb: 1 }}>
                    <Typography><strong>MongoDB Integration</strong>: Complete backend-frontend synchronization with real-time data persistence</Typography>
                  </Box>
                  <Box component="li" sx={{ mb: 1 }}>
                    <Typography><strong>CRUD Operations</strong>: Full Create, Read, Update, Delete functionality for domain management</Typography>
                  </Box>
                  <Box component="li" sx={{ mb: 1 }}>
                    <Typography><strong>Standardized Dropdowns</strong>: Predefined Type and Category options for consistent data entry</Typography>
                  </Box>
                  <Box component="li" sx={{ mb: 1 }}>
                    <Typography><strong>Enhanced Form Validation</strong>: Robust client-side and server-side validation with error handling</Typography>
                  </Box>
                </Box>

                <Typography variant="subtitle1" gutterBottom sx={{ color: '#003366', fontWeight: 600, mt: 2 }}>
                  Dashboard Enhancements
                </Typography>
                <Box component="ul" sx={{ pl: 3, mb: 2 }}>
                  <Box component="li" sx={{ mb: 1 }}>
                    <Typography><strong>Auto-Refresh Functionality</strong>: Real-time data updates every 30 seconds for current metrics</Typography>
                  </Box>
                  <Box component="li" sx={{ mb: 1 }}>
                    <Typography><strong>Enhanced Typography</strong>: Significantly improved font sizes for better readability and accessibility</Typography>
                  </Box>
                  <Box component="li" sx={{ mb: 1 }}>
                    <Typography><strong>Performance Optimization</strong>: Eliminated infinite refresh loops and improved loading efficiency</Typography>
                  </Box>
                </Box>

                <Typography variant="subtitle1" gutterBottom sx={{ color: '#003366', fontWeight: 600, mt: 2 }}>
                  Data Catalog User Experience
                </Typography>
                <Box component="ul" sx={{ pl: 3, mb: 2 }}>
                  <Box component="li" sx={{ mb: 1 }}>
                    <Typography><strong>Collapsible Help Section</strong>: "How It Works" section now collapsed by default for cleaner interface</Typography>
                  </Box>
                  <Box component="li" sx={{ mb: 1 }}>
                    <Typography><strong>Improved Accessibility</strong>: Enhanced keyboard navigation and screen reader support</Typography>
                  </Box>
                  <Box component="li" sx={{ mb: 1 }}>
                    <Typography><strong>Interactive Elements</strong>: Smooth animations and hover effects for better user engagement</Typography>
                  </Box>
                </Box>

                <Typography variant="subtitle1" gutterBottom sx={{ color: '#003366', fontWeight: 600, mt: 2 }}>
                  Technology Stack
                </Typography>
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>Frontend</Typography>
                    <Box component="ul" sx={{ pl: 3 }}>
                      <Box component="li"><Typography variant="body2">React 18 with TypeScript</Typography></Box>
                      <Box component="li"><Typography variant="body2">Material-UI (MUI) v5</Typography></Box>
                      <Box component="li"><Typography variant="body2">React Router v6</Typography></Box>
                      <Box component="li"><Typography variant="body2">Recharts for data visualization</Typography></Box>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>Backend</Typography>
                    <Box component="ul" sx={{ pl: 3 }}>
                      <Box component="li"><Typography variant="body2">Node.js with Express.js</Typography></Box>
                      <Box component="li"><Typography variant="body2">MongoDB with Mongoose ODM</Typography></Box>
                      <Box component="li"><Typography variant="body2">JWT Authentication</Typography></Box>
                      <Box component="li"><Typography variant="body2">RESTful API Design</Typography></Box>
                    </Box>
                  </Grid>
                </Grid>

                <Typography variant="h6" gutterBottom sx={{ color: '#003366', fontWeight: 600, mt: 3 }}>
                  Key Features
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <Card elevation={2} sx={{ height: '100%' }}>
                      <CardContent>
                        <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, color: '#003366' }}>
                          USCIS Application Management
                        </Typography>
                        <Typography variant="body2">
                          Complete benefit application lifecycle tracking with real-time analytics and AI/ML insights.
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Card elevation={2} sx={{ height: '100%' }}>
                      <CardContent>
                        <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, color: '#003366' }}>
                          Data Strategy & Planning
                        </Typography>
                        <Typography variant="body2">
                          Strategic priority management with team capacity planning and ROI analysis.
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Card elevation={2} sx={{ height: '100%' }}>
                      <CardContent>
                        <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, color: '#003366' }}>
                          Advanced Data Catalog
                        </Typography>
                        <Typography variant="body2">
                          AI-powered asset discovery with interactive data lineage visualization.
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>

                <Box sx={{ mt: 3, p: 2, backgroundColor: '#e3f2fd', borderRadius: 1, border: '1px solid #bbdefb' }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#1565c0', mb: 1 }}>
                    💡 Development Highlights:
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    • 50 files changed with 16,257+ lines of new code<br/>
                    • Complete MongoDB integration with CRUD operations<br/>
                    • Enhanced accessibility and Section 508 compliance<br/>
                    • Real-time dashboard updates and performance optimization<br/>
                    • Comprehensive documentation and GitHub integration
                  </Typography>
                </Box>
              </Box>
            </TabPanel>

            {/* Tab 7: Executive Summary */}
            <TabPanel value={tabValue} index={7} id={tabId}>
              <Typography variant="h5" gutterBottom sx={{ color: '#003366', fontWeight: 600 }}>
                Executive Summary & Development Roadmap
              </Typography>
              
              <Typography paragraph>
                Comprehensive executive overview of the USCIS Data Literacy Support Platform, including development strategy, team collaboration, and November 19, 2025 demo preparation.
              </Typography>

              <Box sx={{ 
                maxHeight: '70vh', 
                overflow: 'auto', 
                border: '1px solid #e0e0e0', 
                borderRadius: 2, 
                p: 3,
                backgroundColor: '#fafafa'
              }}>
                {/* Executive Overview */}
                <Typography variant="h6" gutterBottom sx={{ color: '#003366', fontWeight: 600 }}>
                  Executive Overview
                </Typography>
                
                <Typography paragraph>
                  The <strong>USCIS Data Literacy Support Platform</strong> is an enterprise-grade data governance and analytics solution designed to transform how USCIS manages data strategy, portfolio operations, and team capacity planning. Built with modern MERN stack technology and AI-first principles, this platform serves as the central hub for data governance, quality management, and strategic decision-making.
                </Typography>

                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item xs={6} md={3}>
                    <Card elevation={2}>
                      <CardContent sx={{ textAlign: 'center' }}>
                        <Typography variant="h4" sx={{ color: '#003366', fontWeight: 'bold' }}>
                          25+
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Major Features
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Card elevation={2}>
                      <CardContent sx={{ textAlign: 'center' }}>
                        <Typography variant="h4" sx={{ color: '#4caf50', fontWeight: 'bold' }}>
                          $24K
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Cost Savings
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Card elevation={2}>
                      <CardContent sx={{ textAlign: 'center' }}>
                        <Typography variant="h4" sx={{ color: '#ff9800', fontWeight: 'bold' }}>
                          159,900%
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          ROI
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Card elevation={2}>
                      <CardContent sx={{ textAlign: 'center' }}>
                        <Typography variant="h4" sx={{ color: '#2196f3', fontWeight: 'bold' }}>
                          100%
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          508 Compliant
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>

                {/* Key Features */}
                <Typography variant="h6" gutterBottom sx={{ color: '#003366', fontWeight: 600, mt: 3 }}>
                  Comprehensive Feature List
                </Typography>

                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, color: '#003366' }}>
                      Executive Dashboards
                    </Typography>
                    <Box component="ul" sx={{ pl: 3 }}>
                      <Box component="li"><Typography variant="body2">Data Strategy Operations Center (DSOC)</Typography></Box>
                      <Box component="li"><Typography variant="body2">MOC Portfolio Management Executive View</Typography></Box>
                      <Box component="li"><Typography variant="body2">Data Quality Dashboard with Trends</Typography></Box>
                      <Box component="li"><Typography variant="body2">Team Utilization Analytics</Typography></Box>
                      <Box component="li"><Typography variant="body2">Compliance Monitoring Dashboard</Typography></Box>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, color: '#003366' }}>
                      Data Governance Modules
                    </Typography>
                    <Box component="ul" sx={{ pl: 3 }}>
                      <Box component="li"><Typography variant="body2">Data Domains Management (CRUD)</Typography></Box>
                      <Box component="li"><Typography variant="body2">Data Catalog with Advanced Search</Typography></Box>
                      <Box component="li"><Typography variant="body2">Data Lineage Visualization (D3.js)</Typography></Box>
                      <Box component="li"><Typography variant="body2">Data Quality Metrics & Scoring</Typography></Box>
                      <Box component="li"><Typography variant="body2">Compliance Framework Tracking</Typography></Box>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, color: '#003366' }}>
                      MOC Portfolio & Program Management
                    </Typography>
                    <Box component="ul" sx={{ pl: 3 }}>
                      <Box component="li"><Typography variant="body2">Multi-Portfolio Dashboard (4 portfolios)</Typography></Box>
                      <Box component="li"><Typography variant="body2">KPI/OKR Management with Click-to-Edit</Typography></Box>
                      <Box component="li"><Typography variant="body2">Risk Management (5 categories)</Typography></Box>
                      <Box component="li"><Typography variant="body2">Innovation Pipeline (AI-First tracking)</Typography></Box>
                      <Box component="li"><Typography variant="body2">Milestone & Project Tracking</Typography></Box>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, color: '#003366' }}>
                      Data Strategy Planning
                    </Typography>
                    <Box component="ul" sx={{ pl: 3 }}>
                      <Box component="li"><Typography variant="body2">Priority Intake Form (4-step wizard)</Typography></Box>
                      <Box component="li"><Typography variant="body2">Epic Management (5 epics per program)</Typography></Box>
                      <Box component="li"><Typography variant="body2">Story Tracking with Progress</Typography></Box>
                      <Box component="li"><Typography variant="body2">Team Sprint Utilization (46 members)</Typography></Box>
                      <Box component="li"><Typography variant="body2">Capacity Planning & Forecasting</Typography></Box>
                    </Box>
                  </Grid>
                </Grid>

                {/* November 19 Demo */}
                <Box sx={{ mt: 3, p: 2, backgroundColor: '#e3f2fd', borderRadius: 1, border: '1px solid #bbdefb' }}>
                  <Typography variant="h6" gutterBottom sx={{ color: '#1565c0', fontWeight: 600 }}>
                    🎯 November 19, 2025 Demo Preparation
                  </Typography>
                  <Typography variant="body2" paragraph>
                    <strong>Demo Agenda (45 minutes):</strong>
                  </Typography>
                  <Box component="ol" sx={{ pl: 3 }}>
                    <Box component="li"><Typography variant="body2">Executive Overview (5 min) - ROI and business value</Typography></Box>
                    <Box component="li"><Typography variant="body2">Data Strategy Operations Center (10 min) - Live demo</Typography></Box>
                    <Box component="li"><Typography variant="body2">MOC Portfolio Management (8 min) - AI readiness tracking</Typography></Box>
                    <Box component="li"><Typography variant="body2">Data Strategy Planning (8 min) - Epic management</Typography></Box>
                    <Box component="li"><Typography variant="body2">Data Quality & Analytics (7 min) - Quality trends</Typography></Box>
                    <Box component="li"><Typography variant="body2">Integration Roadmap (5 min) - Databricks, Tableau, JIRA</Typography></Box>
                    <Box component="li"><Typography variant="body2">Q&A and Next Steps (2 min)</Typography></Box>
                  </Box>
                </Box>

                {/* Data Integration Roadmap */}
                <Typography variant="h6" gutterBottom sx={{ color: '#003366', fontWeight: 600, mt: 3 }}>
                  Data Integration Roadmap
                </Typography>

                <TableContainer component={Paper} sx={{ mb: 3 }}>
                  <Table size="small">
                    <TableHead sx={{ backgroundColor: '#003366' }}>
                      <TableRow>
                        <TableCell sx={{ color: 'white', fontWeight: 600 }}>Week</TableCell>
                        <TableCell sx={{ color: 'white', fontWeight: 600 }}>Integration</TableCell>
                        <TableCell sx={{ color: 'white', fontWeight: 600 }}>Status</TableCell>
                        <TableCell sx={{ color: 'white', fontWeight: 600 }}>Priority</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        <TableCell>Nov 4-8</TableCell>
                        <TableCell>Databricks setup</TableCell>
                        <TableCell><Chip label="Planned" size="small" color="warning" /></TableCell>
                        <TableCell><Chip label="Critical" size="small" color="error" /></TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Nov 11-15</TableCell>
                        <TableCell>Tableau embedding</TableCell>
                        <TableCell><Chip label="Planned" size="small" color="warning" /></TableCell>
                        <TableCell><Chip label="High" size="small" color="warning" /></TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Nov 11-15</TableCell>
                        <TableCell>JIRA sync</TableCell>
                        <TableCell><Chip label="Planned" size="small" color="warning" /></TableCell>
                        <TableCell><Chip label="High" size="small" color="warning" /></TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Nov 11-15</TableCell>
                        <TableCell>Confluence sync</TableCell>
                        <TableCell><Chip label="Planned" size="small" color="warning" /></TableCell>
                        <TableCell><Chip label="Medium" size="small" color="info" /></TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Nov 18-19</TableCell>
                        <TableCell>SharePoint (if time)</TableCell>
                        <TableCell><Chip label="Planned" size="small" color="warning" /></TableCell>
                        <TableCell><Chip label="Low" size="small" color="default" /></TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>

                {/* AttainX Innovation Lab */}
                <Typography variant="h6" gutterBottom sx={{ color: '#003366', fontWeight: 600, mt: 3 }}>
                  AttainX Innovation Lab Development Strategy
                </Typography>

                <Typography paragraph>
                  The <strong>AttainX Innovation Lab</strong> serves as USCIS's rapid prototyping and innovation center, leveraging AI-assisted development tools (Windsurf Cascade) to accelerate delivery while maintaining government-grade quality standards.
                </Typography>

                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item xs={12} md={6}>
                    <Card elevation={2} sx={{ height: '100%' }}>
                      <CardContent>
                        <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, color: '#003366' }}>
                          AI-First Development
                        </Typography>
                        <Typography variant="body2">
                          Leverage Windsurf Cascade for 5× faster development with AI code generation, testing, and documentation while maintaining human oversight for quality and compliance.
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Card elevation={2} sx={{ height: '100%' }}>
                      <CardContent>
                        <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, color: '#003366' }}>
                          Rapid Prototyping
                        </Typography>
                        <Typography variant="body2">
                          2-week sprint cycles with daily incremental deployments and continuous stakeholder feedback for agile development.
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Card elevation={2} sx={{ height: '100%' }}>
                      <CardContent>
                        <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, color: '#003366' }}>
                          Government-Grade Quality
                        </Typography>
                        <Typography variant="body2">
                          Section 508 compliance mandatory, security-first architecture, and comprehensive audit trails for all operations.
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Card elevation={2} sx={{ height: '100%' }}>
                      <CardContent>
                        <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, color: '#003366' }}>
                          Cost Efficiency
                        </Typography>
                        <Typography variant="body2">
                          99.94% cost reduction vs traditional development with minimal infrastructure overhead and cloud-native architecture.
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>

                {/* Success Metrics */}
                <Box sx={{ mt: 3, p: 2, backgroundColor: '#f1f8e9', borderRadius: 1, border: '1px solid #c5e1a5' }}>
                  <Typography variant="h6" gutterBottom sx={{ color: '#558b2f', fontWeight: 600 }}>
                    📊 Success Metrics & KPIs
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2" paragraph>
                        <strong>Development Metrics:</strong>
                      </Typography>
                      <Box component="ul" sx={{ pl: 3, mb: 0 }}>
                        <Box component="li"><Typography variant="body2">Features Delivered: 25/25 ✅</Typography></Box>
                        <Box component="li"><Typography variant="body2">Section 508 Compliance: 100% ✅</Typography></Box>
                        <Box component="li"><Typography variant="body2">Performance (Load Time): 1.5s ✅</Typography></Box>
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2" paragraph>
                        <strong>Business Metrics:</strong>
                      </Typography>
                      <Box component="ul" sx={{ pl: 3, mb: 0 }}>
                        <Box component="li"><Typography variant="body2">Data Quality: 41% → 94% ✅</Typography></Box>
                        <Box component="li"><Typography variant="body2">Cost Savings: $2.4M ✅</Typography></Box>
                        <Box component="li"><Typography variant="body2">Team Utilization: 47.5% 🔄</Typography></Box>
                      </Box>
                    </Grid>
                  </Grid>
                </Box>

                {/* Full Documentation Link */}
                <Box sx={{ mt: 3, p: 2, backgroundColor: '#fff3e0', borderRadius: 1, border: '1px solid #ffe0b2', textAlign: 'center' }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#e65100', mb: 1 }}>
                    📄 Complete Documentation Available
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    For the full executive summary with detailed development roadmap, team collaboration guidelines, change control processes, and data integration architecture, see <strong>EXECUTIVE_SUMMARY.md</strong> in the project repository.
                  </Typography>
                </Box>
              </Box>
            </TabPanel>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
}

export default About;
